import { articleDraftKey, isBlankArticleHtml, parseArticleDraft, type ArticleDraftFields } from '../lib/article-draft';
import {
  buildOwnerOptions,
  ownerHint,
  ownerPayload,
  ownerSelectValue,
  parseOwnerValue,
} from '../lib/article-owner';
import {
  MAX_VERIFY_DAYS,
  VERIFY_CUSTOM,
  VERIFY_NONE,
  VERIFY_PRESETS,
  clampVerifyEvery,
  isCustomVerifyEvery,
  isValidVerifyEvery,
  verifyPeriodLabel,
  verifyPresetLabel,
  verifyReminderHint,
  verifySelectValue,
} from '../lib/verify-interval';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Empty, Image, InputNumber, message, Modal, Popconfirm, Popover, Select, Skeleton } from 'antd';
import {
  AuditOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PictureOutlined,
  ReadOutlined,
  PrinterOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  StarFilled,
  StarOutlined,
  SmileOutlined,
  TeamOutlined,
  UserOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  NotificationOutlined,
  ReadFilled,
} from '@ant-design/icons';
import DOMPurify from 'dompurify';

import { authApi } from '../lib/authApi';
import { ApiError } from '../lib/api';
import {
  attachmentsApi,
  editorApi,
  readsApi,
  wikiApi,
  wikiAdminApi,
  rolesAdminApi,
  type ArticleFrontmatterInput,
  type WikiArticleDetailDto,
  type WikiArticleListItem,
} from '../lib/wikiApi';
import { makeSectionComparator } from '../lib/sectionOrder';
import { EmojiPicker } from '../components/EmojiPicker';
import { pushRecent } from '../lib/recent';
import { CommentsThread } from '../components/CommentsThread';
import { VersionHistory } from '../components/VersionHistory';
import { SuggestEditModal } from '../components/SuggestEditModal';
import { DecisionTreeRenderer } from '../components/DecisionTreeRenderer';
import { createRoot, type Root } from 'react-dom/client';
import { FkTableViewer } from '../editor/fk-table';
import { FkVideoPlayer } from '../components/FkVideoPlayer';
import { ReactionBar } from '../components/ReactionBar';
import { BroadcastModal } from '../components/BroadcastModal';
import { googleSheetEmbed, toEmbedTarget } from '../lib/embed';
import { hydrateCheckBlocks } from '../lib/hydrate-check';
import { Tabs } from 'antd';
import { AttachmentsSection, KpiLinkWidget } from '../components/ArticleExtras';
import { PageTree } from '../components/PageTree';
import { LeaseContractPicker } from '../components/LeaseContractWidget';
import { ProjectPlanWidget, ProjectApprovalWidget } from '../components/OpeningPlanWidget';
import { renderSectionIcon } from '../lib/sectionIcons';
import { TiptapEditor } from '../editor/TiptapEditor';
import type { Editor as TiptapInstance } from '../editor/TiptapEditor';
import { scrollToCollabPeer, useCollab, type CollabSession } from '../editor/useCollab';
import { htmlToMarkdown, markdownToHtml } from '../editor/markdown-html';
import { setDeletedFileIds } from '../editor/deleted-files';
import { diskTrashedMessage } from '../lib/disk-trash';
import { AiRewriteModal } from '../components/AiRewriteModal';
import { setToolbarAiActions } from '../editor/TiptapToolbar';
import { kbAckColor, kbApi, type KbArticleDetail } from '../lib/api/kb';
import { Button as DsButton, Tag as DsTag } from '../components/ds';
import { CourseOutlineModal, ConflictsModal } from '../components/ArticleExtras';
import './KnowledgeBase.css';

/**
 * Детальная страница статьи базы знаний (макет 4 кита «FISHKA HUB v2» —
 * богатый ридер).
 *
 *   ┌─ кнопка «← База знаний» ──────────────────────────────────┐
 *   │ хлебные крошки (Главная › блок › … › статья)               │
 *   ├──────────────────────────────────┬────────────────────────┤
 *   │ бейджи (категория · обязательно · │ sticky-рейл:           │
 *   │   тип материала)                  │  ▸ действия (редакт/   │
 *   │ H1 (indigo 32/800)                │     ознакомление/PDF/  │
 *   │ мета-строка (автор · версия ·     │     AI/история)        │
 *   │   дата · чтение · статус)          │  ▸ метаданные (блоки/  │
 *   │   в hairline-рамке                │     категория/теги/    │
 *   │ тело (bodyHtml + чек-листы)       │     backlinks/похожие) │
 *   │ KPI-виджет · decision-tree        │                        │
 *   │ вложения · ack-блок · комментарии │                        │
 *   └──────────────────────────────────┴────────────────────────┘
 *
 * Все данные/мутации сохранены: getArticle, mark-read, ack, комментарии,
 * вложения (upload/delete), decision-tree, история версий, предложить
 * правку, KPI-виджет. DOMPurify санитизирует bodyHtml. Автор и версия —
 * реальные поля DTO (author = User.fullName, version = max версии); если
 * пусты — просто не показываем (без фейка). Inline-редактирование секций
 * из мока кита НЕ переносилось.
 */

/** Пункт оглавления, собранный из заголовка тела статьи. */
interface TocItem {
  id: string;
  text: string;
  level: 2 | 3;
}

// Отступ якоря под sticky-хедер (top:84 у рейла) — заголовок не уезжает под шапку.
const SCROLL_OFFSET = 88;

// Статьи БЗ, в тело которых подмешиваются интерактивные виджеты «Открытие
// точки». Если slug статьи в проде иной — добавить сюда.
const LEASE_CONTRACT_SLUGS = new Set(['podpisat-dogovor-arendy']);          // Блок A
const PROJECT_PLAN_SLUGS = new Set(['peredat-plan-v-uk-i-proektirovshiku']); // Блок B
const PROJECT_APPROVAL_SLUGS = new Set(['utverdit-proekt']);                 // Блок C

// Slug из текста заголовка для client-side anchor'а: кириллица → translit,
// прочее → дефисы. id нужен только в пределах страницы, поэтому достаточно
// устойчивого читаемого префикса (уникальность добивается индексом в эффекте).
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
  й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
  э: 'e', ю: 'yu', я: 'ya',
};
function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .split('')
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

// Типы материала и статусы для панели метаданных инлайн-редактора
// (синхронно с FrontmatterDto бэкенда).
const META_TYPES: ReadonlyArray<readonly [NonNullable<ArticleFrontmatterInput['type']>, string]> = [
  ['article', 'Статья'],
  ['instruction', 'Инструкция'],
  ['checklist', 'Чек-лист'],
  ['regulation', 'Регламент'],
  ['faq', 'FAQ'],
  ['standard', 'Стандарт'],
  ['publication', 'Публикация'],
];
/** Структура шаблона по жанру (Diátaxis) — подсказка автору в панели свойств. */
const TYPE_STRUCTURE: Record<NonNullable<ArticleFrontmatterInput['type']>, string> = {
  article: 'Контекст → Почему так → Примеры',
  instruction: 'Цель → Когда применять → Шаги → Частые ошибки → Связанные',
  checklist: 'Пункты с чекбоксами → критерии зачёта',
  regulation: 'Область → Требования → Ответственность → Контроль → История изменений',
  faq: 'Вопрос → Короткий ответ → Подробности',
  standard: 'Область → Требования → Ответственность → Контроль → История изменений',
  publication: 'Лид → Обложка → Тело → Что меняется / дедлайн',
  document_template: 'Назначение → Как заполнять → Файл',
  wizard_step: 'Шаг мастера: что сделать → проверка → следующий шаг',
  decision_tree: 'Вопрос → Варианты ответа → Ветка решения',
};

/** Рекомендуемый срок перепроверки по жанру (дни) — из плана базы знаний. */
const TYPE_VERIFY_DEFAULT: Record<NonNullable<ArticleFrontmatterInput['type']>, number> = {
  article: 365,
  instruction: 90,
  checklist: 180,
  regulation: 180,
  faq: 365,
  standard: 180,
  publication: 365,
  document_template: 365,
  wizard_step: 365,
  decision_tree: 365,
};

const META_STATUSES: ReadonlyArray<readonly [NonNullable<ArticleFrontmatterInput['status']>, string]> = [
  ['DRAFT', 'Черновик'],
  ['REVIEW', 'На согласовании'],
  ['PUBLISHED', 'Опубликовано'],
  ['ARCHIVED', 'Архив'],
];

export function ArticlePage(): JSX.Element {
  const { modal } = App.useApp();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'article', slug],
    queryFn: () => wikiApi.getArticle(slug!),
    enabled: !!slug,
    // Предзагруженная статья (hover/соседи раздела) считается свежей 60с —
    // открывается мгновенно без повторного запроса и белого экрана.
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  // Файлы из текста, которых больше нет (удалены с Диска / вложение удалено): карточки в редакторе
  // помечаются «Файл удалён». Читалке отдельно ничего не нужно — бэк уже поправил bodyHtml.
  const deletedFileIds = data?.deletedFileIds;
  useEffect(() => {
    setDeletedFileIds(deletedFileIds);
    return () => setDeletedFileIds(undefined);
  }, [deletedFileIds]);

  const meQuery = useQuery({
    queryKey: ['wiki-auth', 'me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });

  // Список разделов БЗ — нужен только для заголовка/иконки левой панели-дерева
  // («{Название раздела} / Раздел базы знаний»). Кэшируется надолго, дёшево.
  const blocksQuery = useQuery({
    queryKey: ['wiki', 'blocks'],
    queryFn: () => wikiApi.listBlocks(),
    staleTime: 5 * 60_000,
  });
  const canEdit = meQuery.data?.canCreate ?? false;
  const canAdmin = meQuery.data?.canAdmin ?? false;
  const isCrm = meQuery.data?.type === 'crm_user';
  const qc = useQueryClient();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // Сайдбар «Страницы раздела» — сохраняем позицию скролла между переходами по
  // статьям (при навигации data на миг пуст → сайдбар размонтируется и скролл
  // сбрасывался в начало; теперь восстанавливаем из sessionStorage по блоку).
  const treeNavRef = useRef<HTMLElement | null>(null);
  const treeBlock = data?.blocks?.[0];
  useEffect(() => {
    const el = treeNavRef.current;
    if (!el || !treeBlock) return;
    const key = `fk-tree-scroll:${treeBlock}`;
    const saved = sessionStorage.getItem(key);
    if (saved) {
      // дерево статей грузится асинхронно — восстанавливаем после двух кадров.
      requestAnimationFrame(() => requestAnimationFrame(() => { el.scrollTop = Number(saved); }));
    }
    const onScroll = (): void => sessionStorage.setItem(key, String(el.scrollTop));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [treeBlock]);
  // Оглавление статьи (TOC): строится из реальных h2/h3 тела (anchor'ы на
  // клиенте, бэкенд не трогаем). activeId — подсвеченный пункт (scrollspy).
  const [toc, setToc] = useState<TocItem[]>([]);
  const [activeTocId, setActiveTocId] = useState<string | null>(null);
  // Лайтбокс для картинок тела (галерея + одиночные <img>): клик открывает
  // antd Image-preview с зумом/поворотом/флипом (как в «онлайн репутация new»).
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  // Mark-read при первом монтировании (только для CRM-юзеров с прогрессом).
  // Идемпотентно — backend инкрементит readCount, не дублирует записи.
  useEffect(() => {
    if (slug && isCrm && data) {
      readsApi.markRead(slug).catch(() => {
        /* не критично — silent fail */
      });
    }
  }, [slug, isCrm, data]);

  // ChecklistBlock (`[ ]`): markdown-it рендерит task-list как disabled-
  // чекбоксы. Делаем их интерактивными с сохранением в localStorage
  // per-статья (без backend — работает и оффлайн в PWA).
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !slug || !data) return;
    const boxes = Array.from(root.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
    if (boxes.length === 0) return;
    const key = `wiki-checklist:${slug}`;
    let saved: boolean[] = [];
    try {
      saved = JSON.parse(localStorage.getItem(key) || '[]') as boolean[];
    } catch {
      saved = [];
    }
    const persist = (): void => {
      const state = boxes.map((b) => b.checked);
      localStorage.setItem(key, JSON.stringify(state));
    };
    boxes.forEach((cb, i) => {
      cb.disabled = false;
      if (typeof saved[i] === 'boolean') cb.checked = saved[i];
      cb.style.cursor = 'pointer';
      cb.addEventListener('change', persist);
    });
    return () => boxes.forEach((cb) => cb.removeEventListener('change', persist));
  }, [slug, data]);

  // Таблицы в статье: сортировка по клику на заголовок (toggle ▲/▼, числа
  // сортируются как числа) + строка фильтра над таблицей (прячет строки без
  // совпадения). Прогрессивное обогащение уже отрендеренного HTML — markdown
  // не трогаем. Флаг data-fk-enhanced защищает от повторного навешивания.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const tables = Array.from(root.querySelectorAll<HTMLTableElement>('table'));
    const cleanups: Array<() => void> = [];
    const num = (s: string): number | null => {
      const v = parseFloat(s.replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
      return Number.isNaN(v) ? null : v;
    };
    tables.forEach((table) => {
      if (table.dataset.fkEnhanced === '1') return;
      const thead = table.tHead;
      const tbody = table.tBodies[0];
      const headRow = thead?.rows[0];
      if (!thead || !tbody || !headRow) return;
      const dataRows = (): HTMLTableRowElement[] => Array.from(tbody.rows);
      if (dataRows().length < 2) return; // нечего сортировать/фильтровать
      table.dataset.fkEnhanced = '1';

      // Строка фильтра над таблицей.
      const wrap = document.createElement('div');
      wrap.style.cssText = 'margin:10px 0 6px;';
      const input = document.createElement('input');
      input.type = 'search';
      input.placeholder = '🔎 Фильтр по таблице…';
      input.style.cssText =
        'width:100%;max-width:340px;padding:6px 10px;border:1px solid #e5e7eb;border-radius:8px;font-size:13px;outline:none;';
      wrap.appendChild(input);
      table.parentNode?.insertBefore(wrap, table);
      const onFilter = (): void => {
        const q = input.value.trim().toLowerCase();
        dataRows().forEach((r) => {
          const text = r.textContent?.toLowerCase() ?? '';
          r.style.display = !q || text.includes(q) ? '' : 'none';
        });
      };
      input.addEventListener('input', onFilter);
      cleanups.push(() => {
        input.removeEventListener('input', onFilter);
        wrap.remove();
      });

      // Сортировка по клику на заголовок.
      const ths = Array.from(headRow.cells);
      let sortCol = -1;
      let sortDir: 1 | -1 = 1;
      const sortBy = (col: number): void => {
        if (sortCol === col) sortDir = sortDir === 1 ? -1 : 1;
        else {
          sortCol = col;
          sortDir = 1;
        }
        const rows = dataRows();
        rows.sort((a, b) => {
          const ta = a.cells[col]?.textContent?.trim() ?? '';
          const tb = b.cells[col]?.textContent?.trim() ?? '';
          const na = num(ta);
          const nb = num(tb);
          const cmp = na !== null && nb !== null ? na - nb : ta.localeCompare(tb, 'ru');
          return cmp * sortDir;
        });
        rows.forEach((r) => tbody.appendChild(r));
        ths.forEach((th, i) => {
          const arrow = th.querySelector('.fk-sort-arrow');
          if (arrow) arrow.textContent = i === col ? (sortDir === 1 ? ' ▲' : ' ▼') : '';
        });
      };
      ths.forEach((th, i) => {
        th.style.cursor = 'pointer';
        th.style.userSelect = 'none';
        if (!th.querySelector('.fk-sort-arrow')) {
          const arrow = document.createElement('span');
          arrow.className = 'fk-sort-arrow';
          arrow.style.cssText = 'color:#FF6900;font-size:11px;';
          th.appendChild(arrow);
        }
        const handler = (): void => sortBy(i);
        th.addEventListener('click', handler);
        cleanups.push(() => th.removeEventListener('click', handler));
      });
    });
    return () => {
      cleanups.forEach((fn) => fn());
      tables.forEach((t) => {
        delete t.dataset.fkEnhanced;
      });
    };
  }, [slug, data]);

  // Google-таблицы: ссылка на Google Sheets, стоящая ОТДЕЛЬНОЙ СТРОКОЙ (абзац,
  // в котором только эта ссылка), превращается в живой встроенный просмотр
  // (iframe /preview). Источник — Google; правка только там, вики показывает
  // свежие данные. Ссылки внутри текста/списков НЕ трогаем (остаются ссылками).
  // Нужен доступ к листу «по ссылке: просмотр» (без API-ключей и оплаты).
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const anchors = Array.from(
      root.querySelectorAll<HTMLAnchorElement>('a[href*="docs.google.com/spreadsheets"]'),
    );
    const created: HTMLElement[] = [];
    const cleanups2: Array<() => void> = [];
    anchors.forEach((a) => {
      const p = a.closest('p');
      // только «одинокая» ссылка-абзац (весь текст абзаца = текст ссылки)
      if (!p || (p.textContent || '').trim() !== (a.textContent || '').trim()) return;
      // Адрес разбираем как URL: только https://docs.google.com/spreadsheets/…
      // (lib/embed.ts, аудит 19.10.2026). Остальное остаётся обычной ссылкой.
      const sheet = googleSheetEmbed(a.href);
      if (!sheet) return;
      const src = sheet.src;

      const box = document.createElement('div');
      box.className = 'fk-gsheet-embed';
      box.style.cssText =
        'margin:14px 0;border:1px solid #e6e6ea;border-radius:12px;overflow:hidden;background:#fff;';
      const bar = document.createElement('div');
      bar.style.cssText =
        'display:flex;justify-content:space-between;align-items:center;gap:8px;padding:6px 10px;background:#f7f7f9;border-bottom:1px solid #eee;font-size:12px;color:#6b7280;flex-wrap:wrap;';
      const label = document.createElement('span');
      label.textContent = '📊 Google-таблица';
      label.style.cssText = 'white-space:nowrap;';

      const viewport = document.createElement('div');
      viewport.style.cssText = 'position:relative;overflow:auto;height:520px;';
      const iframe = document.createElement('iframe');
      // Песочница: Google нужны скрипты, свой origin (вход в аккаунт) и
      // всплывающие окна (ссылки из ячеек); остальное — нет. Адрес вики в
      // Referer Google не уходит.
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
      iframe.referrerPolicy = 'no-referrer';
      iframe.src = src;
      iframe.loading = 'lazy';
      iframe.style.cssText = 'border:0;display:block;width:100%;height:100%;transform-origin:0 0;';

      // Зум: scale<1 показывает больше столбцов/строк; ширину/высоту iframe
      // увеличиваем на 1/scale, чтобы контент заполнял вьюпорт без полей.
      let scale = 1;
      const applyScale = (): void => {
        iframe.style.transform = `scale(${scale})`;
        iframe.style.width = `${100 / scale}%`;
        iframe.style.height = `${100 / scale}%`;
        pct.textContent = `${Math.round(scale * 100)}%`;
      };
      const mkBtn = (txt: string, title: string, fn: () => void): HTMLButtonElement => {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = txt;
        b.title = title;
        b.style.cssText =
          'border:1px solid #e0e0e6;background:#fff;border-radius:7px;min-width:28px;height:26px;padding:0 7px;cursor:pointer;font-size:13px;color:#251D4A;line-height:1;';
        b.addEventListener('click', fn);
        return b;
      };
      const pct = document.createElement('span');
      pct.style.cssText = 'min-width:38px;text-align:center;font-variant-numeric:tabular-nums;';
      const zoomOut = mkBtn('−', 'Уменьшить (видно больше)', () => {
        scale = Math.max(0.4, Math.round((scale - 0.1) * 10) / 10);
        applyScale();
      });
      const zoomIn = mkBtn('+', 'Увеличить', () => {
        scale = Math.min(1.5, Math.round((scale + 0.1) * 10) / 10);
        applyScale();
      });

      const expand = mkBtn('⛶', 'Во весь экран', () => {
        if (document.fullscreenElement) document.exitFullscreen();
        else box.requestFullscreen?.();
      });
      const onFs = (): void => {
        const full = document.fullscreenElement === box;
        viewport.style.height = full ? '100vh' : '520px';
        box.style.margin = full ? '0' : '14px 0';
      };
      document.addEventListener('fullscreenchange', onFs);

      const open = document.createElement('a');
      open.href = sheet.openHref;
      open.target = '_blank';
      open.rel = 'noopener noreferrer';
      open.textContent = 'В Google ↗';
      open.style.cssText = 'color:#FF6900;font-weight:600;text-decoration:none;white-space:nowrap;';

      const tools = document.createElement('span');
      tools.style.cssText = 'display:flex;align-items:center;gap:5px;';
      tools.append(zoomOut, pct, zoomIn, expand, open);
      bar.append(label, tools);
      applyScale();
      viewport.appendChild(iframe);
      box.appendChild(bar);
      box.appendChild(viewport);

      cleanups2.push(() => document.removeEventListener('fullscreenchange', onFs));
      p.parentNode?.insertBefore(box, p.nextSibling);
      p.style.display = 'none'; // прячем исходную ссылку-абзац (дубль)
      created.push(box);
      a.dataset.gsheetEmbedded = '1';
    });
    return () => {
      cleanups2.forEach((fn) => fn());
      created.forEach((el) => el.remove());
      anchors.forEach((a) => {
        if (a.dataset.gsheetEmbedded) {
          delete a.dataset.gsheetEmbedded;
          const p = a.closest('p');
          if (p) (p as HTMLElement).style.display = '';
        }
      });
    };
  }, [slug, data]);

  // Лайтбокс: делегируем клик по <img> в теле. Если картинка внутри галереи —
  // собираем все картинки галереи (свайп между ними), иначе одиночное превью.
  // Курсор-указатель проставляем тут же, чтобы было понятно что кликабельно.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
    imgs.forEach((img) => (img.style.cursor = 'zoom-in'));
    const onClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'IMG') return;
      // Картинки внутри ссылок не перехватываем — пусть работает навигация.
      if (target.closest('a')) return;
      const img = target as HTMLImageElement;
      const gallery = img.closest('.fk-gallery');
      e.preventDefault();
      if (gallery) {
        const srcs = Array.from(gallery.querySelectorAll('img')).map((i) => i.src);
        const idx = srcs.indexOf(img.src);
        setLightbox({ images: srcs, index: idx < 0 ? 0 : idx });
      } else {
        setLightbox({ images: [img.src], index: 0 });
      }
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [slug, data]);

  // Сбор оглавления: ищем h2/h3 в теле, проставляем недостающие id (slug из
  // текста + порядковый индекс для уникальности) и кладём scroll-margin, чтобы
  // sticky-хедер не перекрывал якорь. Зависит от slug+контента — пересобирается
  // при смене статьи. Сбрасываем при размонтировании/отсутствии тела.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !slug || !data) {
      setToc([]);
      setActiveTocId(null);
      return;
    }
    const headings = Array.from(root.querySelectorAll<HTMLHeadingElement>('h2, h3'));
    const used = new Set<string>();
    const items: TocItem[] = [];
    headings.forEach((h, i) => {
      const text = (h.textContent || '').trim();
      if (!text) return;
      let id = h.id;
      if (!id) {
        const base = slugifyHeading(text) || 'section';
        id = base;
        // гарантируем уникальность даже при одинаковых заголовках
        while (used.has(id)) id = `${base}-${i}`;
        h.id = id;
      }
      used.add(id);
      h.style.scrollMarginTop = `${SCROLL_OFFSET}px`;
      items.push({ id, text, level: h.tagName === 'H3' ? 3 : 2 });
    });
    setToc(items);
    setActiveTocId(items[0]?.id ?? null);
  }, [slug, data]);

  // Применяем ширину/выравнивание картинок из query-параметров src
  // (?w=420&a=center) — так ресайз/выравнивание из редактора отображается
  // в читалке (vault — markdown, ширина зашита в URL).
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
    for (const img of imgs) {
      const q = (img.getAttribute('src') || '').split('?')[1];
      if (!q) continue;
      const p = new URLSearchParams(q);
      const w = parseInt(p.get('w') || '', 10);
      const a = p.get('a');
      if (Number.isFinite(w) && w > 0) { img.style.width = `${w}px`; img.style.height = 'auto'; img.style.maxWidth = '100%'; }
      if (a === 'full') { img.style.width = '100%'; }
      if (a === 'center') { img.style.display = 'block'; img.style.marginLeft = 'auto'; img.style.marginRight = 'auto'; }
      else if (a === 'right') { img.style.display = 'block'; img.style.marginLeft = 'auto'; img.style.marginRight = '0'; }
    }
  }, [data]);

  // Оживляем интерактивные таблицы (fk-table): backend отдаёт <div data-fk-table>
  // со статической таблицей, здесь монтируем в них React-вьюер (фильтр/
  // сортировка/группировка/скрытие на лету; ячейки read-only).
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-fk-table]'));
    const mounted: Root[] = [];
    for (const node of nodes) {
      const raw = node.getAttribute('data-fk-table');
      if (!raw) continue;
      node.innerHTML = '';
      const r = createRoot(node);
      r.render(<FkTableViewer raw={raw} />);
      mounted.push(r);
    }
    return () => {
      // Асинхронно — нельзя unmount синхронно внутри cleanup того же рендера.
      mounted.forEach((r) => setTimeout(() => r.unmount(), 0));
    };
  }, [data]);

  // Видео с нашего S3 (фаза 0.2): бэк отдаёт <figure data-video> с нативным
  // <video>; здесь монтируем свой плеер (постер, скорость, позиция,
  // «обрабатывается» пока транскод не готов).
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('figure[data-video]'));
    const mounted: Root[] = [];
    for (const node of nodes) {
      const id = node.getAttribute('data-video') || '';
      if (!/^[0-9a-fA-F-]{8,64}$/.test(id)) continue;
      const caption = node.getAttribute('data-caption') || '';
      node.innerHTML = '';
      const r = createRoot(node);
      r.render(<FkVideoPlayer id={id} caption={caption} />);
      mounted.push(r);
    }
    return () => {
      mounted.forEach((r) => setTimeout(() => r.unmount(), 0));
    };
  }, [data]);

  // Блоки фазы 1.7: встраивания (iframe только по allow-list), вкладки
  // (antd Tabs), KPI-виджет. Старый iframe-хак для ссылок на Google Sheets
  // остаётся для статей без блока embed.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !data) return;
    const mounted: Root[] = [];
    for (const node of Array.from(root.querySelectorAll<HTMLElement>('div[data-embed]'))) {
      const target = toEmbedTarget(node.getAttribute('data-embed') || '');
      if (!target) continue; // остаётся ссылка
      const height = Math.min(1200, Math.max(200, parseInt(node.getAttribute('data-height') || '', 10) || target.height));
      const iframe = document.createElement('iframe');
      iframe.src = target.src;
      iframe.loading = 'lazy';
      iframe.allowFullscreen = true;
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-forms');
      iframe.setAttribute('title', 'Встраивание');
      iframe.style.cssText = `width:100%;height:${height}px;border:0;border-radius:12px;background:var(--fk-bg-subtle)`;
      node.innerHTML = '';
      node.appendChild(iframe);
    }
    for (const node of Array.from(root.querySelectorAll<HTMLElement>('div[data-tabs]'))) {
      const tabs = Array.from(node.children).filter((c) => c.classList.contains('fk-tab')) as HTMLElement[];
      if (tabs.length === 0) continue;
      const items = tabs.map((t, i) => ({
        key: String(i),
        label: t.getAttribute('data-title') || `Вкладка ${i + 1}`,
        html: (t.querySelector('.fk-tab-body') as HTMLElement | null)?.innerHTML ?? '',
      }));
      node.innerHTML = '';
      const r = createRoot(node);
      r.render(<ArticleTabs items={items} />);
      mounted.push(r);
    }
    for (const node of Array.from(root.querySelectorAll<HTMLElement>('div[data-kpi]'))) {
      const code = node.getAttribute('data-kpi') || '';
      if (!code) continue;
      node.innerHTML = '';
      const r = createRoot(node);
      r.render(<KpiLinkWidget kpiCode={code} />);
      mounted.push(r);
    }
    const unhydrateChecks = hydrateCheckBlocks(root);
    return () => {
      unhydrateChecks();
      mounted.forEach((r) => setTimeout(() => r.unmount(), 0));
    };
  }, [data]);

  // Scrollspy: IntersectionObserver подсвечивает активный пункт TOC. Берём
  // верхний из пересекающих окно заголовков; rootMargin поджимает нижнюю
  // границу, чтобы переключение шло у верха вьюпорта. Чистим observer при
  // смене статьи/размонтировании.
  useEffect(() => {
    if (toc.length < 2) return;
    const targets = toc
      .map((t) => document.getElementById(t.id))
      .filter((el): el is HTMLElement => el != null);
    if (targets.length === 0) return;
    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.set(e.target.id, e.boundingClientRect.top);
          else visible.delete(e.target.id);
        }
        if (visible.size > 0) {
          // самый верхний из видимых заголовков
          const topId = [...visible.entries()].sort((a, b) => a[1] - b[1])[0][0];
          setActiveTocId(topId);
        }
      },
      { rootMargin: `-${SCROLL_OFFSET}px 0px -65% 0px`, threshold: 0 },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [toc]);

  // Архивировать (= «удалить» статью): уходит в Архив (Администрирование),
  // оттуда — «Удалить навсегда». Файл из vault удаляется, восстановимо через git.
  // Slug статьи-замены при архивации (фаза 3.4) — по прямой ссылке на архив
  // читатель увидит «заменена на …».
  const [archiveReplaceSlug, setArchiveReplaceSlug] = useState('');
  const archiveMutation = useMutation({
    mutationFn: () => editorApi.archive(data!.id, archiveReplaceSlug.trim() || null),
    onSuccess: () => {
      message.success('Статья перемещена в архив');
      navigate('/knowledge');
    },
    onError: (e: Error) => message.error(e.message),
  });

  const ackMutation = useMutation({
    mutationFn: () => readsApi.ack(slug!),
    onSuccess: async () => {
      message.success('Подтверждение зафиксировано');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // ─── Инлайн-редактирование тела статьи прямо на странице ──────────────
  // Для редакторов (canEdit) — кнопка «Редактировать» переключает тело из
  // режима чтения в наш Tiptap (единый редактор). Сохранение =
  // editorApi.update → авто-бэкап текущей редакции в WikiArticleVersion, т.е.
  // «выпуск новой версии». Метаданные (blocks/type/category/tags/requiresAck/
  // effective_from/excerpt) НЕ редактируем здесь — пробрасываем как есть, чтобы
  // update их не затёр.
  const [editing, setEditing] = useState(false);
  /**
   * Совместное редактирование (фаза 4.7). Подключаемся только в режиме
   * правки и только если сервер поднял Hocuspocus (`collabEnabled` из
   * /wiki-auth/me). Если WS недоступен — сессия сама уходит в 'offline',
   * и редактор работает как до 4.7 (см. editor/useCollab.ts).
   */
  const collab = useCollab(
    data?.id,
    editing && canEdit && !!meQuery.data?.collabEnabled,
    meQuery.data?.fullName ?? '',
  );
  const collabLive = collab.state === 'ready';
  // Инстанс Tiptap: при collab заменяем текст командой редактора (setEditHtml
  // в Y.Doc не попадёт), поэтому AI-переписи нужна прямая ссылка.
  const editorRef = useRef<TiptapInstance | null>(null);

  const [editTitle, setEditTitle] = useState('');
  const [editHtml, setEditHtml] = useState('');
  // AI «переписать по стайлгайду» (фаза 4.4) — diff-превью, применение в редактор.
  const [rewriteOpen, setRewriteOpen] = useState(false);
  // Метаданные статьи — теперь редактируются прямо в инлайн-режиме (единый
  // редактор, без отдельной страницы /edit). Остальные поля (роли/notify/
  // wizard/downloadable/kpi) пробрасываются как есть из data — см. releaseMutation.
  const [editType, setEditType] = useState<ArticleFrontmatterInput['type']>('article');
  const [editBlocks, setEditBlocks] = useState<string[]>([]);
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  // Справочник тегов прячем за «+ тег»: в узком рельсе сотня чипов не помещается.
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [editExcerpt, setEditExcerpt] = useState('');
  const [editRequiresAck, setEditRequiresAck] = useState(false);
  const [editEffectiveFrom, setEditEffectiveFrom] = useState('');
  const [editStatus, setEditStatus] = useState<NonNullable<ArticleFrontmatterInput['status']>>('PUBLISHED');
  // «Что изменилось» (фаза 3.5): пишется в версию; обязательно для опубликованного стандарта с requires_ack.
  const [editSummary, setEditSummary] = useState('');
  // Отправка на проверку: рецензент выбирается в модалке.
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewerId, setReviewerId] = useState<string | null>(null);
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [returnNote, setReturnNote] = useState<string | null>(null);
  // Версия статьи на момент входа в правку (optimistic locking): уходит в
  // PATCH как baseVersion — если за время правки кто-то выпустил свою версию,
  // бэк вернёт 409 и мы не затрём чужие изменения молча.
  const [editBaseVersion, setEditBaseVersion] = useState<number | null>(null);
  // Кому видна статья (роли). Пусто = видно всем, у кого есть доступ к разделу.
  const [editRoles, setEditRoles] = useState<string[]>([]);
  // Обложка (URL вложения) и иконка-эмодзи — фаза 0.1 «Красивые статьи».
  const [editCover, setEditCover] = useState('');
  const [editIcon, setEditIcon] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  // Владелец, срок проверки и прочие свойства панели (фаза 0.5).
  const [editOwnerId, setEditOwnerId] = useState<string | null>(null);
  // Владелец-отдел (код): у статьи либо человек (editOwnerId), либо отдел — выбор одного снимает другого.
  const [editOwnerDept, setEditOwnerDept] = useState<string | null>(null);
  const [editOwnerName, setEditOwnerName] = useState<string | null>(null);
  const [editVerifyEvery, setEditVerifyEvery] = useState<number | null>(null);
  // «Свой интервал…» выбран (или у статьи нестандартный интервал, например 45 дней).
  const [verifyCustom, setVerifyCustom] = useState(false);
  const [editDownloadable, setEditDownloadable] = useState(false);
  const [editNotify, setEditNotify] = useState(false);
  const [editKpi, setEditKpi] = useState('');
  const [ownerSearch, setOwnerSearch] = useState('');
  // Отложенная публикация (фаза 1.1): локальное datetime → ISO при сохранении.
  const [editPublishAt, setEditPublishAt] = useState('');
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const rolesQuery = useQuery({
    queryKey: ['wiki', 'roles-for-visibility'],
    queryFn: () => rolesAdminApi.list(),
    enabled: editing,
    retry: false,
    staleTime: 5 * 60_000,
  });
  // Существующие категории выбранного блока — для выбора в поле «Категория»
  // (datalist: можно выбрать из списка или ввести новую). Грузим только в
  // режиме правки при выбранном блоке.
  const editCategoriesQuery = useQuery({
    queryKey: ['wiki', 'categories', editBlocks[0] ?? null],
    queryFn: () => wikiApi.listCategories(editBlocks[0]),
    enabled: editing && editBlocks.length > 0,
    staleTime: 60_000,
  });
  const editCategoryNames = useMemo(() => {
    const order = new Map<string, number>();
    for (const c of editCategoriesQuery.data ?? []) {
      const prev = order.get(c.name);
      if (prev == null || c.sortOrder < prev) order.set(c.name, c.sortOrder);
    }
    return Array.from(new Set((editCategoriesQuery.data ?? []).map((c) => c.name))).sort(
      makeSectionComparator(order),
    );
  }, [editCategoriesQuery.data]);
  // Каталог тегов — выбор из существующих. Создание нового тега — только
  // админам БЗ (kb:admin); остальные выбирают из готового списка.
  const tagsCatalogQuery = useQuery({
    queryKey: ['wiki', 'tags', 'catalog'],
    queryFn: () => wikiApi.listTags(),
    enabled: editing,
    staleTime: 60_000,
  });
  const [newTagName, setNewTagName] = useState('');
  const createTagMutation = useMutation({
    mutationFn: (name: string) => wikiAdminApi.createTag({ name }),
    onSuccess: (t: { code: string }) => {
      qc.invalidateQueries({ queryKey: ['wiki', 'tags', 'catalog'] });
      if (t?.code) setEditTags((prev) => (prev.includes(t.code) ? prev : [...prev, t.code]));
      setNewTagName('');
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Загрузка файла с компьютера прямо в инлайн-редакторе (картинка/файл/PDF
  // через «/»-меню → «Изображение»/«Файл»). Грузим во вложения этой статьи.
  const articleIdForUpload = data?.id;
  const uploadFile = useCallback(
    async (file: File) => {
      if (!articleIdForUpload) return null;
      // Единый параллельный путь загрузки (части в несколько потоков) + прогресс.
      const key = `upload-${file.name}`;
      try {
        message.open({ key, type: 'loading', content: `Загрузка ${file.name}…`, duration: 0 });
        const r = await attachmentsApi.uploadParallel(articleIdForUpload, file, (p) => {
          message.open({ key, type: 'loading', content: `Загрузка ${file.name} — ${p}%`, duration: 0 });
        }, true /* inline — вставка прямо в текст, не в «Вложения» */);
        message.open({ key, type: 'success', content: 'Файл загружен', duration: 2 });
        return { url: attachmentsApi.downloadUrl(r.id), fileName: r.fileName, kind: r.kind, id: r.id };
      } catch (e) {
        message.open({ key, type: 'error', content: (e as Error).message, duration: 3 });
        return null;
      }
    },
    [articleIdForUpload],
  );

  // Обложка: грузим картинку как inline-вложение статьи и запоминаем её URL
  // во frontmatter `cover`. Убрать обложку = очистить поле (файл остаётся
  // во вложениях, чистится сборщиком «сирот» при сохранении).
  const uploadCover = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.warning('Обложка — только картинка (jpg, png, webp)');
      return;
    }
    setCoverUploading(true);
    try {
      const r = await uploadFile(file);
      if (r) setEditCover(r.url);
    } finally {
      setCoverUploading(false);
    }
  }, [uploadFile]);

  // ─── Черновик правки в localStorage ────────────────────────────────────
  // Текст, настройки и исходная версия сохраняются отдельно для пользователя.
  // При отмене/успехе очищаем; ошибку localStorage показываем в редакторе.
  const draftFields = useMemo<ArticleDraftFields>(() => ({ title: editTitle, type: editType, blocks: editBlocks, category: editCategory, tags: editTags, excerpt: editExcerpt, requiresAck: editRequiresAck, roles: editRoles, cover: editCover, icon: editIcon, ownerId: editOwnerId, ownerDept: editOwnerDept, ownerName: editOwnerName, verifyEvery: editVerifyEvery, downloadable: editDownloadable, notify: editNotify, kpi: editKpi, publishAt: editPublishAt, effectiveFrom: editEffectiveFrom, status: editStatus, summary: editSummary }), [editTitle, editType, editBlocks, editCategory, editTags, editExcerpt, editRequiresAck, editRoles, editCover, editIcon, editOwnerId, editOwnerDept, editOwnerName, editVerifyEvery, editDownloadable, editNotify, editKpi, editPublishAt, editEffectiveFrom, editStatus, editSummary]);
  const draftKey = slug && meQuery.data?.userId ? articleDraftKey(meQuery.data.userId, slug) : null;
  const [draftError, setDraftError] = useState(false);
  const draftActive = useRef(false);
  const readDraft = () => {
    try { return draftKey ? parseArticleDraft(localStorage.getItem(draftKey)) : null; } catch { return null; }
  };

  /** Удаляет черновик (выпуск версии / отмена / отказ от восстановления). */
  const clearDraft = (): void => {
    draftActive.current = false;
    if (!draftKey) return;
    try {
      localStorage.removeItem(draftKey);
    } catch {
      /* молча */
    }
  };

  // Сохраняем весь документ, а перед закрытием вкладки принудительно сбрасываем debounce.
  useEffect(() => {
    if (!editing || !draftKey) return;
    const persist = () => {
      if (!draftActive.current) return;
      try {
        localStorage.setItem(draftKey, JSON.stringify({ html: editHtml, fields: draftFields, savedAt: Date.now(), baseVersion: editBaseVersion ?? undefined }));
        setDraftError(false);
      } catch { setDraftError(true); }
    };
    const timer = window.setTimeout(persist, 1200);
    window.addEventListener('pagehide', persist);
    const beforeUnload = (event: BeforeUnloadEvent) => { persist(); event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => { persist(); window.clearTimeout(timer); window.removeEventListener('pagehide', persist); window.removeEventListener('beforeunload', beforeUnload); };
  }, [editing, draftKey, editHtml, draftFields, editBaseVersion]);

  // Вход в режим правки — сидируем редактор из текущего тела (md → html).
  // Вынесено из startEditing, чтобы модалка восстановления черновика могла
  // выбрать html ДО монтирования Tiptap (initialHtml применяется на mount).
  const enterEditMode = (a: WikiArticleDetailDto, html: string, baseVersion?: number): void => {
    draftActive.current = true;
    setEditTitle(a.title);
    setEditHtml(html);
    // Запоминаем «от какой версии правим»: для восстановленного черновика —
    // версию на момент его создания (могла быть старее текущей), иначе —
    // текущую версию статьи. Уйдёт в PATCH как baseVersion (конфликт → 409).
    setEditBaseVersion(baseVersion ?? a.version ?? null);
    setEditType((a.type as ArticleFrontmatterInput['type']) ?? 'article');
    setEditBlocks(a.blocks ?? []);
    setEditCategory(a.categoryName ?? '');
    setEditTags(a.tags.map((t) => t.code));
    setEditExcerpt(a.excerpt ?? '');
    setEditRequiresAck(!!a.requiresAck);
    setEditRoles(a.visibleToRoles ?? []);
    setEditCover(a.coverUrl ?? '');
    setEditIcon(a.icon ?? '');
    setEditOwnerId(a.ownerId ?? null);
    setEditOwnerDept(a.ownerDeptCode ?? null);
    setEditOwnerName(a.ownerName ?? null);
    setEditVerifyEvery(a.verifyEveryDays ?? null);
    setVerifyCustom(isCustomVerifyEvery(a.verifyEveryDays ?? null));
    setEditDownloadable(!!a.downloadable);
    setEditNotify(!!a.notifyOnUpdate);
    setEditKpi(a.linkedKpiCode ?? '');
    setEditPublishAt(a.publishAt ? toLocalDateTimeInput(a.publishAt) : '');
    setEditEffectiveFrom(a.effectiveFrom ? a.effectiveFrom.slice(0, 10) : '');
    setEditStatus((a.status as NonNullable<ArticleFrontmatterInput['status']>) ?? 'PUBLISHED');
    setEditing(true);
  };

  // Кнопка «Редактировать»: если в localStorage лежит черновик, отличающийся
  // от текущего тела статьи — спрашиваем, восстановить ли его.
  const startEditing = (a: WikiArticleDetailDto): void => {
    const baseHtml = a.bodyMd ? markdownToHtml(a.bodyMd) : '';
    let draft = readDraft();
    // Пустой черновик поверх статьи с текстом — след открывшегося пустым редактора (16.10.2026):
    // не предлагаем «Восстановить», иначе статья снова откроется пустой.
    if (draft && isBlankArticleHtml(draft.html) && a.bodyMd.trim()) {
      clearDraft();
      draft = null;
    }
    if (draft) {
      const when = new Date(draft.savedAt).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
      modal.confirm({
        title: 'Найден несохранённый черновик',
        content: `Найден несохранённый черновик от ${when}. Восстановить?`,
        okText: 'Восстановить',
        cancelText: 'Удалить черновик',
        // Черновик мог быть создан от более старой версии статьи — правим
        // именно от неё, чтобы конфликт-защита сработала честно.
        onOk: () => {
          enterEditMode(a, draft.html, draft.baseVersion);
          setEditTitle(draft.fields.title);
          setEditType(draft.fields.type);
          setEditBlocks(draft.fields.blocks);
          setEditCategory(draft.fields.category);
          setEditTags(draft.fields.tags);
          setEditExcerpt(draft.fields.excerpt);
          setEditRequiresAck(draft.fields.requiresAck);
          setEditRoles(draft.fields.roles);
          setEditCover(draft.fields.cover);
          setEditIcon(draft.fields.icon);
          setEditOwnerId(draft.fields.ownerId);
          setEditOwnerDept(draft.fields.ownerDept);
          setEditOwnerName(draft.fields.ownerName);
          setEditVerifyEvery(draft.fields.verifyEvery);
          setVerifyCustom(isCustomVerifyEvery(draft.fields.verifyEvery));
          setEditDownloadable(draft.fields.downloadable);
          setEditNotify(draft.fields.notify);
          setEditKpi(draft.fields.kpi);
          setEditPublishAt(draft.fields.publishAt);
          setEditEffectiveFrom(draft.fields.effectiveFrom);
          setEditStatus(draft.fields.status);
          setEditSummary(draft.fields.summary);
        },
        // «Нет» = черновик не нужен: удаляем и открываем актуальное тело.
        onCancel: () => {
          clearDraft();
          enterEditMode(a, baseHtml);
        },
      });
      return;
    }
    enterEditMode(a, baseHtml);
  };

  // Авто-вход в редактор для только что созданной статьи (модалка «Новая
  // статья» открывает /article/<slug>?edit=1). Срабатывает один раз, когда
  // данные загрузились и юзер может править; затем убираем ?edit из URL.
  const autoEditRef = useRef(false);
  useEffect(() => {
    if (autoEditRef.current) return;
    if (searchParams.get('edit') !== '1') return;
    if (!data || !canEdit || editing) return;
    autoEditRef.current = true;
    startEditing(data);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, canEdit, editing, searchParams]);

  // Текст, который сейчас В РЕДАКТОРЕ. Источник истины — сам редактор: в совместном режиме текст
  // приходит из Y.Doc без onChange, и `editHtml` мог оставаться пустым, хотя на экране текст есть
  // (владелец 22.10.2026: «Текст статьи пустой» при непустой статье; «Сохранить пустую» стёрла бы её).
  const currentEditHtml = (): string => {
    const ed = editorRef.current;
    return ed && !ed.isDestroyed ? ed.getHTML() : editHtml;
  };

  // Пустой текст поверх статьи с текстом — только осознанно (владелец 16.10.2026: совместный редактор
  // открывался пустым, «Сохранить» выпустил бы пустую версию стандарта и разослал её на подтверждение).
  const saveVersion = (a: WikiArticleDetailDto): void => {
    if (verifyCustom && editVerifyEvery != null && !isValidVerifyEvery(editVerifyEvery)) {
      message.error(`Свой интервал проверки — целое число дней от 1 до ${MAX_VERIFY_DAYS}`);
      return;
    }
    if (verifyCustom && editVerifyEvery == null) {
      message.error('Укажите, через сколько дней проверять статью, или выберите «Не проверять»');
      return;
    }
    if (!htmlToMarkdown(currentEditHtml()).trim() && a.bodyMd.trim()) {
      modal.confirm({
        title: 'Текст статьи пустой',
        content: 'В редакторе нет текста, а в статье он есть. Сохранить пустую версию? Прежний текст останется в истории версий.',
        okText: 'Сохранить пустую',
        okButtonProps: { danger: true },
        cancelText: 'Вернуться к правке',
        onOk: () => releaseMutation.mutate(a),
      });
      return;
    }
    releaseMutation.mutate(a);
  };

  // Сохранение = новая версия. Статус оставляем PUBLISHED (стандарт остаётся
  // опубликованным), frontmatter переносим из текущей статьи без изменений.
  const releaseMutation = useMutation({
    mutationFn: (a: WikiArticleDetailDto) => {
      const bodyMd = htmlToMarkdown(currentEditHtml());
      const frontmatter: ArticleFrontmatterInput = {
        // Редактируемые в панели поля:
        blocks: editBlocks.length > 0 ? editBlocks : undefined,
        category: editCategory.trim(),
        tags: editTags,
        type: editType ?? undefined,
        requires_ack: editRequiresAck,
        effective_from: editEffectiveFrom,
        excerpt: editExcerpt.trim(),
        cover: editCover.trim(),
        icon: editIcon.trim(),
        status: editStatus,
        // Passthrough (не редактируем здесь, но frontmatter заменяется целиком —
        // без явной передачи эти поля затёрлись бы):
        visible_to_roles: editRoles.length > 0 ? editRoles : [],
        notify_on_update: editNotify,
        downloadable: editDownloadable,
        linked_kpi_code: editKpi.trim(),
        // Человек ЛИБО отдел: выбранный вытесняет прежнего, «не задан» снимает обоих.
        ...ownerPayload(editOwnerId, editOwnerDept),
        verify_every_days: editVerifyEvery,
        publish_at: editPublishAt ? new Date(editPublishAt).toISOString() : '',
        wizard_order: a.wizardOrder ?? undefined,
        expected_days: a.expectedDays ?? undefined,
      };
      return editorApi.update(a.id, {
        title: editTitle,
        bodyMd,
        frontmatter,
        // Optimistic locking: версия на момент входа в правку. Если статью
        // успели изменить — бэк вернёт 409, обработка в onError ниже.
        baseVersion: editBaseVersion ?? undefined,
        summary: editSummary.trim() || undefined,
      });
    },
    onSuccess: async (res) => {
      message.success('Новая версия выпущена');
      // Убрали файл из текста → его копия на Диске ушла в Корзину (30 дней на возврат).
      const trashed = diskTrashedMessage(res.diskTrashed);
      if (trashed) message.info(trashed, 7);
      setEditSummary('');
      clearDraft(); // правки сохранены — черновик больше не нужен
      setEditing(false);
      // Обновляем тело + версию в шапке и историю версий.
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'versions'] });
    },
    onError: (e: Error) => {
      // 409 — конфликт одновременной правки: кто-то выпустил версию, пока мы
      // редактировали. Чужие правки НЕ затираем; черновик юзера остаётся в
      // localStorage (автосейв wiki-draft:<slug>) — его можно восстановить
      // при следующем входе в правку и перенести текст вручную.
      if (e instanceof ApiError && e.status === 409) {
        const body = (e.body ?? {}) as {
          updatedBy?: string | null;
          updatedAt?: string | null;
        };
        const who = body.updatedBy ? ` Последним сохранял: ${body.updatedBy}.` : '';
        modal.confirm({
          title: 'Статья изменена другим пользователем',
          content: `Пока вы редактировали, кто-то выпустил новую версию.${who} Откройте актуальную версию и перенесите свои правки — ваш черновик сохранён и будет предложен при следующем входе в редактирование.`,
          okText: 'Открыть актуальную версию',
          cancelText: 'Отмена',
          onOk: async () => {
            // Выходим из правки и перезагружаем статью. Черновик НЕ удаляем —
            // юзер восстановит свой текст через модалку «Найден черновик».
            setEditing(false);
            await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
            await qc.invalidateQueries({ queryKey: ['wiki', 'versions'] });
          },
          // «Отмена» — остаёмся в редакторе (например, чтобы скопировать текст).
        });
        return;
      }
      message.error(e.message);
    },
  });

  // Поиск пользователей для поля «Владелец» (только в правке).
  const ownerOptionsQuery = useQuery({
    queryKey: ['wiki', 'editor-users', ownerSearch],
    queryFn: () => editorApi.searchUsers(ownerSearch),
    enabled: editing,
    staleTime: 60_000,
  });
  // Отделы УК («Поддержка партнёров → Отделы») для поля «Владелец»: их мало, грузим все разом.
  const ownerDeptsQuery = useQuery({
    queryKey: ['wiki', 'editor-departments'],
    queryFn: () => editorApi.ownerDepartments(),
    enabled: editing,
    staleTime: 5 * 60_000,
  });
  const ownerOptions = buildOwnerOptions({
    depts: ownerDeptsQuery.data ?? [],
    users: ownerOptionsQuery.data ?? [],
    search: ownerSearch,
    current: { ownerId: editOwnerId, ownerDept: editOwnerDept, name: editOwnerName },
  });

  // «Подтвердить актуальность» — без новой версии.
  const verifyMutation = useMutation({
    mutationFn: (articleId: string) => editorApi.verify(articleId),
    onSuccess: async () => {
      message.success('Актуальность подтверждена');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  // Избранное (фаза 0.6): звезда в шапке, optimistic-переключение.
  const favMutation = useMutation({
    mutationFn: ({ s, on }: { s: string; on: boolean }) => wikiApi.setFavorite(s, on),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'favorites'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // «Недавние» — localStorage, при открытии статьи.
  useEffect(() => {
    if (data?.slug) pushRecent({ slug: data.slug, title: data.title, icon: data.icon ?? null });
  }, [data?.slug, data?.title, data?.icon]);

  // «Отвязать» рассылку (чтобы разослать заново).
  const unlinkMutation = useMutation({
    mutationFn: (articleId: string) => editorApi.unlinkBroadcast(articleId),
    onSuccess: async () => {
      message.success('Связь с рассылкой снята — можно разослать заново');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const canBroadcast = !!meQuery.data?.canEdit && data?.status === 'PUBLISHED';

  // Workflow с рецензентом (фаза 3.5).
  const canPublish = !!meQuery.data?.canPublish;
  const isReviewer = !!data?.reviewerId && meQuery.data?.userId === data.reviewerId;
  const reviewerOptionsQuery = useQuery({
    queryKey: ['wiki-editor', 'users', reviewerSearch],
    queryFn: () => editorApi.searchUsers(reviewerSearch),
    enabled: reviewModalOpen,
  });
  const requestReviewMutation = useMutation({
    mutationFn: () => editorApi.requestReview(data!.id, reviewerId!),
    onSuccess: async () => {
      message.success('Отправлено на проверку — рецензент получил уведомление');
      setReviewModalOpen(false); setReviewerId(null);
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const approveMutation = useMutation({
    mutationFn: () => editorApi.approveArticle(data!.id),
    onSuccess: async () => {
      message.success('Одобрено и опубликовано');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const returnMutation = useMutation({
    mutationFn: () => editorApi.returnArticle(data!.id, returnNote ?? ''),
    onSuccess: async () => {
      message.success('Возвращена автору на доработку');
      setReturnNote(null);
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Подтвердить актуальность: редактор, владелец-человек или руководитель/участник отдела-владельца.
  const canVerify =
    !!data &&
    !!meQuery.data &&
    (meQuery.data.canEdit || (!!data.ownerId && meQuery.data.userId === data.ownerId) || !!data.viewerInOwnerDept);

  // Оценка времени чтения — из реального текста тела (≈180 слов/мин).
  const readingMin = useMemo(() => {
    if (data?.readingMinutes) return data.readingMinutes;
    if (!data?.bodyMd) return null;
    const words = data.bodyMd.replace(/[#*_>`[\]()-]/g, ' ').split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 180));
  }, [data]);

  // Карта код раздела → русское имя (для рейла «БЛОКИ» — показываем имя, не код).
  // ВАЖНО: хук должен стоять ВЫШЕ ранних return'ов (isLoading/error/!data) —
  // иначе порядок хуков плавает между рендерами и React падает с ошибкой #310
  // (белый экран всей страницы). Зависит только от blocksQuery — не от data.
  const blockNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of blocksQuery.data ?? []) m.set(b.code, b.name);
    return m;
  }, [blocksQuery.data]);

  // ── HUB 2.0 (W2): «Сделать курсом», «Проверить на противоречия»,
  // «Напомнить неподтвердившим». Хуки — выше ранних return'ов.
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const remindMutation = useMutation({
    mutationFn: (articleId: string) => kbApi.remindStandard(articleId),
    onSuccess: (r) => {
      if (r.alreadyToday) message.info('Сегодня по этому стандарту уже напоминали — следующее напоминание завтра');
      else if (r.sent === 0) message.success('Все из аудитории уже подтвердили — напоминать некому');
      else message.success(`Напоминание отправлено: ${r.sent} из ${r.audience}`);
    },
    onError: (e: Error) => message.error(e.message),
  });
  // AI-кнопки тулбара редактора — только пока идёт правка этой статьи.
  useEffect(() => {
    if (!editing) return undefined;
    return setToolbarAiActions({
      rewrite: () => setRewriteOpen(true),
      checkConflicts: canAdmin ? () => setConflictsOpen(true) : undefined,
    });
  }, [editing, canAdmin]);

  // Плавный скролл к заголовку по клику в оглавлении. scroll-margin-top на
  // заголовке (см. эффект сбора TOC) сам учитывает sticky-хедер.
  const scrollToHeading = (id: string): void => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveTocId(id);
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }
  if (error) {
    const is404 = (error as { status?: number }).status === 404;
    return (
      <Alert
        type={is404 ? 'warning' : 'error'}
        showIcon
        message={is404 ? 'Статья не найдена' : 'Не удалось загрузить статью'}
        description={(error as Error).message}
        action={
          <Link to="/" style={{ marginLeft: 8 }}>
            На главную
          </Link>
        }
      />
    );
  }
  if (!data) return <Empty description="Статья пуста" />;

  // Backend уже даёт чистый HTML (markdown-it с html:false). DOMPurify —
  // вторая линия защиты. Разрешаем data-attributes для wiki-link.
  const sanitized = DOMPurify.sanitize(data.bodyHtml, {
    ADD_TAGS: ['video', 'source'],
    ADD_ATTR: [
      'data-wikilink', 'data-block', 'data-src', 'data-callout', 'data-fk-table',
      'data-video', 'data-caption', 'controls', 'preload', 'poster', 'playsinline',
      'data-columns', 'data-toggle', 'data-steps', 'open',
      'data-embed', 'data-height', 'data-tabs', 'data-title', 'data-button', 'data-file', 'data-name', 'data-size', 'data-kpi',
      'data-check', 'data-correct', 'data-explanation',
    ],
    ALLOWED_TAGS: undefined,
  });

  const firstBlock = data.blocks[0];
  // Метаданные раздела для заголовка левой панели-дерева (название + иконка).
  const sectionBlock = firstBlock ? blocksQuery.data?.find((b) => b.code === firstBlock) : undefined;
  // Дерево показываем только если у статьи есть раздел (иначе обычный 2-кол вид).
  const showTree = !!firstBlock;

  return (
    <div>
      {data.status === 'ARCHIVED' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Статья в архиве"
          description={
            data.replacedBySlug ? (
              <span>
                Она заменена: <Link to={`/article/${data.replacedBySlug}`}>открыть актуальную версию</Link>.
              </span>
            ) : (
              'Материал больше не поддерживается и может быть неактуален.'
            )
          }
        />
      )}
      {data.needsReAck && !editing && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Стандарт обновлён — подтвердите ознакомление ещё раз"
          description={data.changelog?.[0] ? `Что изменилось: ${data.changelog[0].summary}` : undefined}
        />
      )}
      {/* Режим правки: тёмная плашка-напоминание (сохранение — кнопками внизу). */}
      {editing && (
        <div style={{ background: 'var(--fk-indigo)', color: '#fff', padding: '8px 14px', borderRadius: 'var(--fk-radius-md)', display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, marginBottom: 14, flexWrap: 'wrap' }}>
          <EditOutlined />
          <b>Режим редактирования</b>
          <span style={{ color: 'rgba(255,255,255,.65)' }}>
            {data.version ? `версия ${data.version} · ` : ''}черновик автоматически сохраняется на этом устройстве
          </span>
        </div>
      )}

      {/* Хлебные крошки: База знаний / Раздел / … / подраздел */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--fk-text-muted)', flexWrap: 'wrap' }}>
        <Link to="/knowledge" style={{ color: 'var(--fk-text-2)' }}>База знаний</Link>
        {firstBlock && (
          <>
            <span>/</span>
            <Link to={`/block/${firstBlock}`} style={{ color: 'var(--fk-text-2)' }}>{sectionBlock?.name ?? firstBlock}</Link>
          </>
        )}
        {data.breadcrumb?.map((b) => (
          <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>/</span>
            <Link to={`/article/${b.slug}`} style={{ color: 'var(--fk-text-2)' }}>{b.title}</Link>
          </span>
        ))}
        {data.categoryName && (
          <>
            <span>/</span>
            <span>{data.categoryName.replace(/^\s*\d+\.\s*/, '')}</span>
          </>
        )}
      </div>

      <div
        className={`article-layout${showTree ? ' article-layout--tree' : ''}${editing ? ' article-layout--edit' : ''}`}
        style={{
          display: 'grid',
          gridTemplateColumns: editing ? 'minmax(0,1fr)' : 'minmax(0,1fr) 320px',
          gap: 28,
          alignItems: 'start',
          marginTop: 12,
        }}
      >
        {/* ─── Основная колонка ─────────────────────────────────── */}
        <article style={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Обложка (hero): картинка 16:9 сверху, как в Notion. В правке —
              кнопки загрузить/убрать поверх. */}
          {(editing ? editCover : data.coverUrl) && (
            <div className="fk-hero-cover" style={{ position: 'relative', marginBottom: 18, borderRadius: 'var(--fk-radius-card)', overflow: 'hidden', background: 'var(--fk-bg-subtle)', aspectRatio: '16 / 6', maxHeight: 320 }}>
              <img src={editing ? editCover : (data.coverUrl ?? '')} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {editing && (
                <div style={{ position: 'absolute', right: 12, bottom: 12, display: 'flex', gap: 8 }}>
                  <label className="fk-meta-chip" style={{ background: 'rgba(255,255,255,.92)', cursor: 'pointer' }}>
                    {coverUploading ? <LoadingOutlined spin /> : <PictureOutlined />} Заменить
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadCover(f); e.target.value = ''; }} />
                  </label>
                  <button type="button" className="fk-meta-chip" style={{ background: 'rgba(255,255,255,.92)' }} onClick={() => setEditCover('')}>
                    <CloseOutlined /> Убрать
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Иконка-эмодзи у заголовка (как в Notion) */}
          {(editing ? editIcon : data.icon) && (
            <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 10 }} aria-hidden>{editing ? editIcon : data.icon}</div>
          )}
          {/* Заголовок: в режиме правки — инлайн-инпут (как в редакторе) */}
          {editing ? (
            <input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Заголовок статьи"
              maxLength={200}
              style={{ width: '100%', fontSize: 'clamp(22px, 6vw, 32px)', fontWeight: 800, color: 'var(--fk-indigo)', lineHeight: 1.2, letterSpacing: -0.5, border: 'none', outline: 'none', background: 'transparent', margin: '0 0 12px', padding: 0, fontFamily: 'inherit', overflowWrap: 'anywhere' }}
            />
          ) : (
            <h1 style={{ fontSize: 'clamp(24px, 6vw, 32px)', fontWeight: 800, margin: '4px 0 0', color: 'var(--fk-indigo)', lineHeight: 1.2, letterSpacing: -0.5, overflowWrap: 'anywhere' }}>
              {data.title}
            </h1>
          )}

          {/* HUB 2.0: мета-строка (тип, статус, владелец, актуальность, просмотры,
              время чтения) и ряд действий. В режиме правки не показываем —
              там свои панели. */}
          {!editing && (
            <>
              <div className="fk-kb-art-meta">
                {data.type === 'decision_tree' ? (
                  <DsTag color="indigo" icon={<ThunderboltOutlined />}>Сценарий</DsTag>
                ) : (
                  <DsTag color="blue">{MATERIAL_LABELS[data.type] ?? 'Статья'}</DsTag>
                )}
                <StatusPill status={data.status} acked={data.isAcked} />
                {data.requiresAck && !data.isAcked && <DsTag color="red">Обязательно к ознакомлению</DsTag>}
                {data.ownerName && (
                  <span title={data.ownerDeptCode ? 'Владелец — отдел: за актуальность отвечают его руководитель и участники' : 'Владелец отвечает за актуальность статьи'}>
                    {data.ownerDeptCode ? <TeamOutlined /> : <UserOutlined />} {data.ownerName}
                  </span>
                )}
                {data.needsVerify ? (
                  <span style={{ color: 'var(--fk-orange)' }}>
                    <ClockCircleOutlined /> Требует проверки
                  </span>
                ) : data.verifiedAt ? (
                  <span style={{ color: 'var(--fk-success-text)' }} title={data.verifiedByName ? `Подтвердил: ${data.verifiedByName}` : undefined}>
                    <SafetyCertificateOutlined /> Проверено {fmtDate(data.verifiedAt)}
                  </span>
                ) : null}
                <span title="Просмотры">
                  <EyeOutlined /> {data.viewCount ?? 0}
                </span>
                {readingMin != null && (
                  <span>
                    <ClockCircleOutlined /> {readingMin} мин
                  </span>
                )}
                {data.version != null && data.version > 0 && (
                  <span>
                    <HistoryOutlined /> v{data.version}.0
                  </span>
                )}
                {data.publishedAt && (
                  <span>
                    <CalendarOutlined /> {fmtDate(data.publishedAt)}
                  </span>
                )}
                {data.author && data.author !== data.ownerName && (
                  <span title="Автор">
                    <AuthorAvatar name={data.author} /> {data.author}
                  </span>
                )}
                {data.status === 'REVIEW' && data.reviewerName && (
                  <span title={data.reviewRequestedAt ? `Отправлено ${fmtDate(data.reviewRequestedAt)}` : undefined}>
                    <AuditOutlined /> На проверке у: {data.reviewerName}
                  </span>
                )}
                {data.newsPostId && (
                  <span title="Новость создана в CRM и разослана в каналы">
                    <SoundOutlined /> Разослано
                  </span>
                )}
              </div>

              <div className="fk-kb-art-actions">
                {canEdit && (
                  <DsButton size="sm" variant="primary" icon={<EditOutlined />} onClick={() => startEditing(data)}>
                    Редактировать
                  </DsButton>
                )}
                {canVerify && data.status === 'PUBLISHED' && (
                  <DsButton
                    size="sm"
                    variant="ghost"
                    icon={<CheckCircleOutlined />}
                    loading={verifyMutation.isPending}
                    onClick={() => verifyMutation.mutate(data.id)}
                    title="Подтвердить, что статья актуальна (без новой версии)"
                  >
                    Подтвердить актуальность
                  </DsButton>
                )}
                {(canEdit || canAdmin) && data.status === 'PUBLISHED' && data.type !== 'decision_tree' && (
                  <DsButton size="sm" variant="ghost" icon={<ReadFilled />} onClick={() => setOutlineOpen(true)} title="AI соберёт черновик курса по этой статье">
                    Сделать курсом
                  </DsButton>
                )}
                {canBroadcast &&
                  (data.newsPostId ? (
                    <DsButton size="sm" variant="ghost" icon={<SoundOutlined />} onClick={() => unlinkMutation.mutate(data.id)} loading={unlinkMutation.isPending} title="Снять связь с рассылкой, чтобы разослать заново">
                      Отвязать рассылку
                    </DsButton>
                  ) : (
                    <DsButton size="sm" variant="ghost" icon={<NotificationOutlined />} onClick={() => setBroadcastOpen(true)} title="Создать новость в CRM и разослать в Telegram/MAX">
                      Разослать
                    </DsButton>
                  ))}
                {meQuery.data?.canCreate && (data.status === 'DRAFT' || data.status === 'REVIEW') && (
                  <DsButton size="sm" variant="ghost" icon={<AuditOutlined />} onClick={() => setReviewModalOpen(true)} title="Назначить рецензента и отправить на проверку">
                    {data.status === 'REVIEW' ? 'Сменить рецензента' : 'На проверку'}
                  </DsButton>
                )}
                {data.status === 'REVIEW' && (canPublish || isReviewer) && (
                  <>
                    <DsButton size="sm" variant="ghost" icon={<CheckOutlined />} loading={approveMutation.isPending} onClick={() => approveMutation.mutate()}>
                      Одобрить
                    </DsButton>
                    <DsButton size="sm" variant="ghost" icon={<CloseOutlined />} onClick={() => setReturnNote('')}>
                      Вернуть
                    </DsButton>
                  </>
                )}
                <DsButton
                  size="sm"
                  variant={data.isFavorite ? 'soft' : 'ghost'}
                  icon={data.isFavorite ? <StarFilled /> : <StarOutlined />}
                  disabled={favMutation.isPending}
                  onClick={() => favMutation.mutate({ s: data.slug, on: !data.isFavorite })}
                >
                  {data.isFavorite ? 'В избранном' : 'В избранное'}
                </DsButton>
                <DsButton size="sm" variant="ghost" icon={<PrinterOutlined />} href={wikiApi.articlePdfUrl(data.slug)} title="Скачать PDF">
                  PDF
                </DsButton>
              </div>
            </>
          )}

          {/* Excerpt — лид-параграф (только в режиме чтения) */}
          {!editing && data.excerpt && (
            <div style={{ margin: '20px 0 0', padding: '12px 16px', borderRadius: 'var(--fk-radius-md)', background: 'var(--fk-bg-subtle)', color: 'var(--fk-text-2)', fontSize: 15, lineHeight: 1.6 }}>
              {data.excerpt}
            </div>
          )}

          {editing ? (
            /* ─── Инлайн-режим правки: наш Tiptap прямо на странице ──── */
            <div className="fk-edit-grid" style={{ marginTop: 22 }}>
              <div className="fk-edit-main">
              {/* Плашка-пояснение про повторное подтверждение (как в ките) */}
              {editRequiresAck && data.status === 'PUBLISHED' && <div style={{ display: 'flex', gap: 11, marginBottom: 16, padding: '12px 15px', border: '1px solid rgba(255,105,0,.3)', borderRadius: 'var(--fk-radius-md)', background: 'var(--fk-orange-soft)', color: 'var(--fk-text)', fontSize: 13.5, lineHeight: 1.55 }}>
                <HistoryOutlined style={{ color: 'var(--fk-orange)', marginTop: 2, flexShrink: 0 }} />
                <span>Сохранение зафиксирует новую версию стандарта и потребует повторного подтверждения у точек.</span>
              </div>}

              {/* Совместное редактирование (фаза 4.7): кто ещё в статье и
                  что с соединением. В офлайне плашки нет — правим как раньше. */}
              <CollabBar session={collab} />
              {draftError && <Alert type="warning" showIcon title="Черновик не сохранён на этом устройстве" description="Хранилище браузера недоступно или заполнено. Сохраните изменения перед закрытием страницы." />}

              {/* Tiptap-редактор тела. Пока ждём первый sync — редактор не
                  монтируем: иначе пришлось бы засеять документ из bodyMd и
                  при позднем подключении текст статьи удвоился бы.
                  key по состоянию — чтобы редактор пересобрался с набором
                  расширений под нужный режим (с Collaboration или без). */}
              {collab.state === 'connecting' ? (
                <div className="fk-card" style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fk-text-2)', fontSize: 13.5 }}>
                  <LoadingOutlined spin style={{ color: 'var(--fk-orange)' }} />
                  Подключаемся к совместному редактированию…
                </div>
              ) : (
                <TiptapEditor
                  key={collabLive ? 'collab' : 'solo'}
                  initialHtml={editHtml}
                  onChange={setEditHtml}
                  placeholder="Текст стандарта…"
                  uploadFile={uploadFile}
                  collab={collabLive ? collab : undefined}
                  onEditorReady={(e) => { editorRef.current = e; }}
                />
              )}


              {/* Кнопки выпуска новой версии / отмены — sticky внизу экрана,
                  чтобы «Сохранить» был всегда под рукой и не приходилось листать. */}
              <div className="br-edit-actions" style={{ position: 'sticky', bottom: 0, zIndex: 20, display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20, padding: '14px 16px', borderTop: '1px solid var(--fk-border)', background: 'var(--fk-surface,#fff)', boxShadow: '0 -6px 18px rgba(20,20,40,.08)', borderRadius: '0 0 12px 12px' }}>
                <button
                  type="button"
                  className="fk-btn fk-btn-primary"
                  style={{ background: 'var(--fk-orange)', borderColor: 'var(--fk-orange)' }}
                  disabled={releaseMutation.isPending || editTitle.trim().length < 2}
                  onClick={() => saveVersion(data)}
                >
                  {releaseMutation.isPending ? <LoadingOutlined spin /> : <HistoryOutlined />} Сохранить изменения
                </button>
                <button type="button" className="fk-btn fk-btn-ghost" disabled={releaseMutation.isPending} onClick={() => setRewriteOpen(true)} title="ИИ поправит статью по вашей просьбе или по стайлгайду и покажет, что изменилось">
                  ✨ ИИ: править статью
                </button>
                <AiRewriteModal slug={data.slug} open={rewriteOpen} onClose={() => setRewriteOpen(false)} getCurrentMd={() => htmlToMarkdown(currentEditHtml())} onApply={(md) => {
                  const html = markdownToHtml(md);
                  // При collab текст живёт в Y.Doc: setEditHtml его не изменит
                  // (и не покажет правку соредакторам) — заменяем командой.
                  if (collabLive && editorRef.current) {
                    editorRef.current.commands.setContent(html, false);
                  } else {
                    setEditHtml(html);
                  }
                }} />
                <button
                  type="button"
                  className="fk-btn fk-btn-ghost"
                  disabled={releaseMutation.isPending}
                  onClick={() => {
                    clearDraft(); // явная отмена — выбрасываем и черновик
                    setEditing(false);
                  }}
                >
                  <CloseOutlined /> Отмена
                </button>
                <Popconfirm
                  title="Удалить статью?"
                  description={
                    <div style={{ maxWidth: 320 }}>
                      <div>Статья уйдёт в Архив (Администрирование). Оттуда её можно восстановить или удалить навсегда.</div>
                      <input
                        value={archiveReplaceSlug}
                        onChange={(e) => setArchiveReplaceSlug(e.target.value)}
                        placeholder="Slug статьи-замены (необязательно)"
                        style={{ marginTop: 8, width: '100%', padding: '4px 8px', border: '1px solid var(--fk-border-strong)', borderRadius: 6, fontFamily: 'inherit' }}
                      />
                    </div>
                  }
                  okText="Удалить"
                  okButtonProps={{ danger: true }}
                  cancelText="Отмена"
                  onConfirm={() => archiveMutation.mutate()}
                >
                  <button
                    type="button"
                    className="fk-btn fk-btn-ghost"
                    style={{ color: 'var(--fk-error)', borderColor: 'rgba(229,57,53,.4)', marginLeft: 'auto' }}
                    disabled={archiveMutation.isPending}
                  >
                    <DeleteOutlined /> Удалить
                  </button>
                </Popconfirm>
              </div>
              </div>

              {/* ─── Свойства статьи: правый рельс, как в дизайн-прототипе ───
                  Редактор слева, frontmatter справа и всегда на виду; на узком
                  экране рельс уезжает под редактор (медиазапрос ниже). */}
              <aside className="fk-props">
                <div className="fk-props-head">Настройки статьи</div>
                <div className="fk-props-note">
                  <EditOutlined /> Изменения применятся после сохранения.
                </div>

<details className="br-props-group"><summary>Формат и оформление</summary>          {editing && !editCover && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <label className="fk-meta-chip" style={{ cursor: 'pointer' }}>
                {coverUploading ? <LoadingOutlined spin /> : <PictureOutlined />} Добавить обложку
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadCover(f); e.target.value = ''; }} />
              </label>
              <Popover trigger="click" placement="bottomLeft" styles={{ container: { padding: 10 } }} content={<EmojiGrid onPick={(e) => setEditIcon(e)} onClear={() => setEditIcon('')} />}>
                <button type="button" className="fk-meta-chip"><SmileOutlined /> {editIcon ? `Иконка ${editIcon}` : 'Добавить иконку'}</button>
              </Popover>
            </div>
          )}
          {editing && editCover && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Popover trigger="click" placement="bottomLeft" styles={{ container: { padding: 10 } }} content={<EmojiGrid onPick={(e) => setEditIcon(e)} onClear={() => setEditIcon('')} />}>
                <button type="button" className="fk-meta-chip"><SmileOutlined /> {editIcon ? `Иконка ${editIcon}` : 'Добавить иконку'}</button>
              </Popover>
            </div>
          )}
                {/* Тип (жанр) + структура шаблона этого жанра */}
                <div className="fk-props-sec">
                  <MetaLabel>Тип (жанр)</MetaLabel>
                  <div className="fk-chips">
                    {META_TYPES.map(([v, l]) => (
                      <button key={v} type="button" onClick={() => { setEditType(v); if (v === 'standard') setEditRequiresAck(true); }} className="fk-meta-chip" data-on={editType === v}>{l}</button>
                    ))}
                  </div>
                  <div className="fk-props-hint">Структура: {TYPE_STRUCTURE[editType ?? 'article']}</div>
                </div>

</details>
<details open className="br-props-group"><summary>Размещение и ответственность</summary>
<div className="fk-props-sec"><MetaLabel>Раздел</MetaLabel><Select mode="multiple" value={editBlocks} onChange={setEditBlocks} options={(blocksQuery.data ?? []).map(b=>({value:b.code,label:b.name}))} style={{width:'100%'}} /></div>
                <div className="fk-props-sec">
                  <MetaLabel>Категория (подраздел)</MetaLabel>
                  <input
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    list="fk-cat-options"
                    placeholder={editCategoryNames.length > 0 ? 'Выберите или введите новую' : 'Напр. «4. Проектирование»'}
                    className="fk-meta-input"
                  />
                  <datalist id="fk-cat-options">
                    {editCategoryNames.map((n) => <option key={n} value={n} />)}
                  </datalist>
                </div>

                {/* Владелец — обязателен для публикации (фаза 0.5). Человек ЛИБО отдел УК. */}
                <div className="fk-props-sec">
                  <MetaLabel>Владелец <span style={{ color: 'var(--fk-error)' }}>*</span></MetaLabel>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    allowClear
                    size="small"
                    value={ownerSelectValue(editOwnerId, editOwnerDept)}
                    placeholder="Не задан — станете вы"
                    filterOption={false}
                    onSearch={setOwnerSearch}
                    onChange={(v, opt) => {
                      const choice = parseOwnerValue(v);
                      const o = (Array.isArray(opt) ? opt[0] : opt) as { short?: string } | undefined;
                      setEditOwnerId(choice.kind === 'user' ? choice.id : null);
                      setEditOwnerDept(choice.kind === 'dept' ? choice.code : null);
                      setEditOwnerName(choice.kind === 'none' ? null : (o?.short ?? null));
                    }}
                    options={ownerOptions}
                    optionLabelProp="short"
                    loading={ownerOptionsQuery.isFetching || ownerDeptsQuery.isFetching}
                    notFoundContent={ownerOptionsQuery.isFetching ? 'Ищем…' : 'Никого не нашли'}
                  />
                  <div className="fk-props-hint">{ownerHint(editOwnerDept)}</div>
                </div>

                {/* Срок проверки актуальности + аудитория */}
                <div className="fk-props-sec">
                  <MetaLabel>Проверять каждые</MetaLabel>
                  <Select
                    style={{ width: '100%' }}
                    size="small"
                    value={verifySelectValue(editVerifyEvery, verifyCustom)}
                    onChange={(v) => {
                      if (v === VERIFY_NONE) {
                        setVerifyCustom(false);
                        setEditVerifyEvery(null);
                      } else if (v === VERIFY_CUSTOM) {
                        setVerifyCustom(true);
                        // Стартовое число — прежний интервал, а если статью не проверяли — дефолт типа.
                        setEditVerifyEvery((cur) => cur ?? TYPE_VERIFY_DEFAULT[editType ?? 'article']);
                      } else {
                        setVerifyCustom(false);
                        setEditVerifyEvery(v as number);
                      }
                    }}
                    options={[
                      ...VERIFY_PRESETS.map((d) => ({ value: d, label: verifyPresetLabel(d) })),
                      { value: VERIFY_CUSTOM, label: 'Свой интервал…' },
                      { value: VERIFY_NONE, label: 'Не проверять' },
                    ]}
                  />
                  {verifySelectValue(editVerifyEvery, verifyCustom) === VERIFY_CUSTOM && (
                    <InputNumber
                      style={{ width: '100%', marginTop: 6 }}
                      size="small"
                      min={1}
                      max={MAX_VERIFY_DAYS}
                      precision={0}
                      value={editVerifyEvery}
                      onChange={(v) => setEditVerifyEvery(clampVerifyEvery(v))}
                      addonAfter="дней"
                      placeholder={`от 1 до ${MAX_VERIFY_DAYS}`}
                      aria-label="Свой интервал проверки, дней"
                    />
                  )}
                  <div className="fk-props-hint">
                    {verifyReminderHint(editVerifyEvery, verifyCustom)}
                  </div>
                  <div className="fk-props-hint">
                    Дефолт для типа «{META_TYPES.find(([v]) => v === (editType ?? 'article'))?.[1]}» — {TYPE_VERIFY_DEFAULT[editType ?? 'article']} дней.
                  </div>
                </div>

</details><details className="br-props-group"><summary>Доступ, теги и описание</summary>                <div className="fk-props-sec">
                  <MetaLabel>Аудитория (кому видна)</MetaLabel>
                  <div className="fk-chips">
                    {(rolesQuery.data ?? []).map((r) => {
                      const on = editRoles.includes(r.code);
                      return (
                        <button key={r.code} type="button" className="fk-meta-chip" data-on={on}
                          onClick={() => setEditRoles((prev) => on ? prev.filter((c) => c !== r.code) : [...prev, r.code])}>
                          {r.name || r.code}
                        </button>
                      );
                    })}
                    {(rolesQuery.data ?? []).length === 0 && (
                      <span className="fk-props-hint">{rolesQuery.isError ? 'Нет доступа к списку ролей' : 'Загрузка ролей…'}</span>
                    )}
                  </div>
                  <div className="fk-props-hint">
                    {editRoles.length === 0 ? 'Не выбрано — видна всем, у кого есть доступ к разделу.' : `Видно только этим ролям: ${editRoles.length}.`}
                  </div>
                </div>

                {/* Теги: выбранные — чипами с ×, остальные прячем за «+ тег»,
                    иначе справочник на 100 тегов растягивает рельс. */}
                <div className="fk-props-sec">
                  <MetaLabel>Теги (≤ 5, из справочника)</MetaLabel>
                  <div className="fk-chips">
                    {editTags.map((code) => {
                      const t = (tagsCatalogQuery.data ?? []).find((x) => x.code === code);
                      return (
                        <button key={code} type="button" className="fk-tag-chip"
                          onClick={() => setEditTags((prev) => prev.filter((c) => c !== code))}
                          title="Убрать тег">
                          #{t?.name || code} <span className="fk-tag-x">×</span>
                        </button>
                      );
                    })}
                    <button type="button" className="fk-meta-chip" data-on={tagPickerOpen} onClick={() => setTagPickerOpen((v) => !v)}>
                      + тег
                    </button>
                  </div>
                  {tagPickerOpen && (
                    <div className="fk-tag-picker">
                      <div className="fk-chips">
                        {(tagsCatalogQuery.data ?? []).filter((t) => !editTags.includes(t.code)).map((t) => (
                          <button key={t.code} type="button" className="fk-meta-chip"
                            disabled={editTags.length >= 5}
                            onClick={() => setEditTags((prev) => prev.length >= 5 ? prev : [...prev, t.code])}>
                            {t.name || t.code}
                          </button>
                        ))}
                        {(tagsCatalogQuery.data ?? []).filter((t) => !editTags.includes(t.code)).length === 0 && (
                          <span className="fk-props-hint">Свободных тегов нет</span>
                        )}
                      </div>
                      {canAdmin ? (
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <input value={newTagName} onChange={(e) => setNewTagName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newTagName.trim()) createTagMutation.mutate(newTagName.trim()); } }}
                            placeholder="Новый тег" className="fk-meta-input" />
                          <button type="button" className="fk-meta-chip" disabled={!newTagName.trim() || createTagMutation.isPending}
                            onClick={() => createTagMutation.mutate(newTagName.trim())}>
                            {createTagMutation.isPending ? <LoadingOutlined spin /> : '+'}
                          </button>
                        </div>
                      ) : (
                        <div className="fk-props-hint" style={{ marginTop: 6 }}>Новые теги добавляет администратор базы знаний.</div>
                      )}
                    </div>
                  )}
                  {editTags.length >= 5 && <div className="fk-props-hint">Больше 5 тегов не ставим — так поиск точнее.</div>}
                </div>

                <div className="fk-props-sec">
                  <MetaLabel>Краткое описание (в карточках)</MetaLabel>
                  <textarea value={editExcerpt} onChange={(e) => setEditExcerpt(e.target.value)} maxLength={280} rows={2}
                    placeholder="Напр. «Пошаговая инструкция приёма заказа»" className="fk-meta-input" style={{ resize: 'vertical' }} />
                </div>

                {/* Тумблеры и служебные поля одной группой */}
</details><details className="br-props-group"><summary>Ознакомление и параметры</summary>                <div className="fk-props-sec fk-props-group">
                  <button type="button" className="fk-props-toggle" data-on={editRequiresAck} onClick={() => setEditRequiresAck((v) => !v)}>
                    <span>Требует ознакомления</span>
                    <span className="fk-props-switch"><span /></span>
                  </button>
                  {editRequiresAck && (
                    <div className="fk-props-row">
                      <span>Действует с</span>
                      <input type="date" value={editEffectiveFrom} onChange={(e) => setEditEffectiveFrom(e.target.value)} className="fk-meta-input" style={{ width: 148 }} />
                    </div>
                  )}
                  <button type="button" className="fk-props-toggle" data-on={editDownloadable} onClick={() => setEditDownloadable((v) => !v)}>
                    <span>Можно скачать PDF</span>
                    <span className="fk-props-switch"><span /></span>
                  </button>
                  <button type="button" className="fk-props-toggle" data-on={editNotify} onClick={() => setEditNotify((v) => !v)}>
                    <span>Уведомлять об обновлении</span>
                    <span className="fk-props-switch"><span /></span>
                  </button>
                  <div className="fk-props-row">
                    <span>Показатель KPI</span>
                    <input value={editKpi} onChange={(e) => setEditKpi(e.target.value)} placeholder="напр. cogs_pct" className="fk-meta-input" style={{ width: 148 }} />
                  </div>
                  <div className="fk-props-row">
                    <span>Адрес статьи</span>
                    <b className="fk-mono">{data.slug}</b>
                  </div>
                </div>

                {/* Отложенная публикация (фаза 1.1) */}
                {editStatus === 'DRAFT' && (
                  <div className="fk-props-sec">
                    <MetaLabel>Дата публикации</MetaLabel>
                    <input type="datetime-local" value={editPublishAt} onChange={(e) => setEditPublishAt(e.target.value)} className="fk-meta-input" />
                    <div className="fk-props-hint">
                      {editPublishAt ? 'Статья выйдет сама в указанное время (проверка каждые 5 минут).' : 'Оставьте пустым — опубликуете вручную.'}
                    </div>
                  </div>
                )}

                {/* Публикация → рассылка тем же контентом в TG/MAX (фаза 1.3) */}
                {editType === 'publication' && canBroadcast && (
                  <div className="fk-props-sec">
                    <button type="button" className="fk-btn fk-btn-primary" style={{ width: '100%', background: 'var(--fk-orange)', borderColor: 'var(--fk-orange)' }}
                      onClick={() => setBroadcastOpen(true)}>
                      <SoundOutlined /> Разослать в TG/MAX
                    </button>
                    <div className="fk-props-hint">Создаст новость в CRM и отправит её по выбранному таргетингу.</div>
                  </div>
                )}

</details>
                {/* Статус и workflow */}
                <div className="fk-props-sec">
                  <MetaLabel>Статус</MetaLabel>
                  <div className="fk-chips">
                    {META_STATUSES.filter(([v]) => v !== 'PUBLISHED' || canPublish || data.status === 'PUBLISHED').map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setEditStatus(v)} className="fk-meta-chip" data-on={editStatus === v}>{l}</button>
                    ))}
                  </div>
                  {!canPublish && data.status !== 'PUBLISHED' && (
                    <div className="fk-props-hint">Публикует носитель права kb:publish — отправьте статью на проверку.</div>
                  )}
                </div>

                {/* «Что изменилось» (фаза 3.5) */}
                {data.status === 'PUBLISHED' && editStatus === 'PUBLISHED' && (
                  <div className="fk-props-sec">
                    <MetaLabel>Что изменилось{editRequiresAck ? ' (обязательно)' : ' (необязательно)'}</MetaLabel>
                    <textarea value={editSummary} onChange={(e) => setEditSummary(e.target.value)} maxLength={500} rows={2}
                      placeholder="Напр. «Добавлен шаг 4: контроль температуры»" className="fk-meta-input" style={{ resize: 'vertical' }} />
                    {editRequiresAck && <div className="fk-props-hint">Читатели подтвердят ознакомление заново.</div>}
                  </div>
                )}

                <div className="fk-props-foot">
                  <div className="fk-props-row"><span>Порядок выпуска</span><b>Черновик → Проверка → Публикация</b></div>
                  {data.reviewerName && <div className="fk-props-row"><span>Рецензент</span><b>{data.reviewerName}</b></div>}
                  {data.readingMinutes ? <div className="fk-props-row"><span>Время чтения</span><b>{data.readingMinutes} мин</b></div> : null}
                </div>
              </aside>

              <style>{`
                .fk-edit-grid{display:grid;grid-template-columns:minmax(0,1fr) 340px;gap:18px;align-items:start}
                .fk-edit-main{min-width:0}
                .fk-props{position:sticky;top:12px;max-height:calc(100vh - 24px);overflow:auto;display:flex;flex-direction:column;gap:14px;padding:16px;border:1px solid var(--fk-border);border-radius:var(--fk-radius-lg,16px);background:var(--fk-surface);box-shadow:var(--fk-shadow-card)}
                .fk-props-head{font-size:11.5px;font-weight:800;color:var(--fk-text-muted);text-transform:uppercase;letter-spacing:.06em}
                .fk-props-note{display:flex;gap:7px;font-size:12px;line-height:1.45;color:var(--fk-text-2);padding:8px 10px;border-radius:10px;background:var(--fk-orange-soft)}
                .fk-props-note .anticon{color:var(--fk-orange);margin-top:2px;flex-shrink:0}
                .fk-props-sec{display:flex;flex-direction:column}
                .fk-props-group{gap:8px;padding-top:12px;border-top:1px solid var(--fk-border)}
                .fk-props-foot{display:flex;flex-direction:column;gap:6px;padding-top:12px;border-top:1px solid var(--fk-border)}
                .fk-props-hint{font-size:11.5px;line-height:1.45;color:var(--fk-text-muted);margin-top:6px}
                .fk-props-row{display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:12.5px;color:var(--fk-text-2)}
                .fk-props-row b{font-weight:700;color:var(--fk-text);text-align:right}
                .fk-mono{font-family:var(--fk-font-mono);font-size:11.5px}
                .fk-chips{display:flex;flex-wrap:wrap;gap:6px}
                .fk-meta-chip{padding:5px 10px;border-radius:999px;border:1px solid var(--fk-border);background:var(--fk-surface);color:var(--fk-text-2);font-family:inherit;font-size:12px;font-weight:600;cursor:pointer;transition:all .1s}
                .fk-meta-chip:hover:not(:disabled){border-color:var(--fk-orange)}
                .fk-meta-chip:disabled{opacity:.45;cursor:not-allowed}
                .fk-meta-chip[data-on="true"]{background:var(--fk-indigo);color:#fff;border-color:transparent}
                .fk-tag-chip{padding:5px 10px;border-radius:999px;border:1px solid transparent;background:var(--fk-orange-soft);color:var(--fk-orange);font-family:inherit;font-size:12px;font-weight:700;cursor:pointer}
                .fk-tag-chip:hover{background:rgba(229,57,53,.10);color:var(--fk-error)}
                .fk-tag-x{opacity:.6;margin-left:2px}
                .fk-tag-picker{margin-top:8px;padding:10px;border:1px dashed var(--fk-border-strong);border-radius:10px;background:var(--fk-bg-subtle)}
                .fk-meta-input{width:100%;border:1px solid var(--fk-border);border-radius:9px;padding:7px 10px;font-family:inherit;font-size:12.5px;color:var(--fk-text);background:var(--fk-surface);outline:none}
                .fk-meta-input:focus{border-color:var(--fk-orange)}
                .fk-props-toggle{display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;padding:8px 10px;border-radius:10px;border:1px solid var(--fk-border);background:var(--fk-surface);cursor:pointer;font-family:inherit;font-size:12.5px;font-weight:600;color:var(--fk-text);text-align:left}
                .fk-props-toggle[data-on="true"]{background:var(--fk-orange-soft);border-color:transparent}
                .fk-props-switch{position:relative;flex-shrink:0;width:34px;height:20px;border-radius:999px;background:var(--fk-border-strong);transition:background .12s}
                .fk-props-toggle[data-on="true"] .fk-props-switch{background:var(--fk-orange)}
                .fk-props-switch>span{position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;background:#fff;transition:left .12s}
                .fk-props-toggle[data-on="true"] .fk-props-switch>span{left:16px}
                @media (max-width: 1100px){
                  .fk-edit-grid{grid-template-columns:1fr}
                  .fk-props{position:static;max-height:none;order:-1}
                }
              `}</style>
            </div>
          ) : (
            <>
              {/* Тело статьи */}
              <div
                ref={contentRef}
                className="wiki-content wiki-content--numbered"
                style={{ fontSize: 16, lineHeight: 1.75, color: 'var(--fk-text)', marginTop: 22 }}
                dangerouslySetInnerHTML={{ __html: sanitized }}
              />

              {/* Лайтбокс: скрытая группа antd Image — превью открываем
                  программно по клику в теле (зум/поворот/флип из коробки). */}
              {lightbox && (
                <div style={{ display: 'none' }}>
                  <Image.PreviewGroup
                    preview={{
                      visible: true,
                      current: lightbox.index,
                      // Без onChange индекс заморожен и стрелки «вперёд/назад»
                      // не переключают картинку — обновляем current сами.
                      onChange: (cur) =>
                        setLightbox((lb) => (lb ? { ...lb, index: cur } : lb)),
                      onVisibleChange: (vis) => { if (!vis) setLightbox(null); },
                    }}
                  >
                    {lightbox.images.map((src, i) => (
                      <Image key={`${src}-${i}`} src={src} />
                    ))}
                  </Image.PreviewGroup>
                </div>
              )}

              {/* Decision tree (Q75-78): bodyHtml выше — вступление, ниже интерактив */}
              {data.type === 'decision_tree' && (
                <div style={{ marginTop: 24 }}>
                  <DecisionTreeRenderer slug={data.slug} />
                </div>
              )}

              {/* Блок A: генератор договора аренды прямо в статье БЗ. Статья —
                  вступление/инструкция (bodyHtml выше), ниже — интерактивный
                  виджет: выбор одобренной заявки → форма → скачать .docx. */}
              {LEASE_CONTRACT_SLUGS.has(data.slug) && (
                <div style={{ marginTop: 24 }}>
                  <LeaseContractPicker />
                </div>
              )}
              {PROJECT_PLAN_SLUGS.has(data.slug) && (
                <div style={{ marginTop: 24 }}>
                  <ProjectPlanWidget />
                </div>
              )}
              {PROJECT_APPROVAL_SLUGS.has(data.slug) && (
                <div style={{ marginTop: 24 }}>
                  <ProjectApprovalWidget />
                </div>
              )}

              {/* KPI-виджет связи SOP↔дашборд (Q73) */}
              {data.linkedKpiCode && <KpiLinkWidget kpiCode={data.linkedKpiCode} />}

              {/* Вложения (Q29-32) — компонент сохраняет upload/delete-логику */}
              <AttachmentsSection
                slug={data.slug}
                articleId={data.id}
                attachments={data.attachments}
                canEdit={canEdit}
              />
            </>
          )}

          {/* Ack-блок для статей с requires_ack (скрыт в режиме правки) */}
          {!editing && data.requiresAck && !data.isAcked && (
            <div className="fk-kb-panel" style={{ marginTop: 28, padding: 20, textAlign: 'center', borderColor: 'rgba(255,105,0,.45)', maxWidth: 880 }}>
              <div style={{ marginBottom: 12, fontWeight: 700 }}>Эту статью нужно подтвердить</div>
              <DsButton variant="primary" size="lg" icon={<CheckCircleOutlined />} disabled={!isCrm} loading={ackMutation.isPending} onClick={() => ackMutation.mutate()}>
                Я ознакомился
              </DsButton>
              {!isCrm && (
                <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 6 }}>
                  Подтверждение пока доступно только пользователям CRM.
                </div>
              )}
            </div>
          )}

          {!editing && data.isAcked && (
            <div style={{ marginTop: 28, padding: '12px 16px', background: 'var(--fk-success-soft)', border: '1px solid rgba(124,179,66,.4)', borderRadius: 'var(--fk-radius-md)', color: 'var(--fk-success-text)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, maxWidth: 880 }}>
              <CheckCircleOutlined /> Вы ознакомились с этой статьёй
            </div>
          )}

          {/* Навигация по соседним статьям раздела (← Предыдущая / Следующая →).
              Порядок — как в дереве «Страницы»: по категориям, внутри — sortOrder. */}
          {!editing && firstBlock && <PrevNextNav blockCode={firstBlock} currentSlug={data.slug} />}

          {/* Реакции (фаза 1.2) + комментарии (Q8/Q46) — скрыты в режиме правки */}
          {!editing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 4px', flexWrap: 'wrap', maxWidth: 880 }}>
              <span style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>Было полезно?</span>
              <ReactionBar targetType="article" targetId={data.id} />
              <span style={{ flex: 1 }} />
              {isCrm && (
                <button type="button" onClick={() => setSuggestOpen(true)} style={{ background: 'none', border: 'none', color: 'var(--fk-orange)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}>
                  Предложить правку
                </button>
              )}
            </div>
          )}
          {!editing && <CommentsThread slug={data.slug} />}
        </article>

        {/* ─── Правый рейл ──────────────────────────────────────── */}
        {/* В режиме правки внешнего рейла НЕТ: правой колонкой работает панель
            свойств внутри .fk-edit-grid. Иначе получалось две правые колонки —
            и на узком экране между ними зияла пустота. */}
        {editing ? null : (
        <aside className="article-rail fk-kb-art-aside">
          {/* HUB 2.0: карточка стандарта — УК видит «Подтвердили по сети» и
              «Напомнить неподтвердившим», остальные — кнопку подтверждения. */}
          {data.requiresAck && (
            <StandardCard
              a={data as KbArticleDetail}
              canAdmin={canAdmin}
              isCrm={isCrm}
              acking={ackMutation.isPending}
              onAck={() => ackMutation.mutate()}
              reminding={remindMutation.isPending}
              onRemind={() => remindMutation.mutate(data.id)}
            />
          )}

          {/* Содержание (TOC) — реальные h2/h3 тела; показываем от 2 пунктов */}
          {toc.length >= 2 && (
            <nav className="fk-kb-panel article-toc" style={{ padding: 18 }} aria-label="Содержание">
              <div className="fk-kb-cap">Содержание</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {toc.map((item) => {
                  const isActive = activeTocId === item.id;
                  return (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={(e) => { e.preventDefault(); scrollToHeading(item.id); }}
                      style={{
                        display: 'block',
                        padding: '3px 0 3px 10px',
                        marginLeft: item.level === 3 ? 12 : 0,
                        borderLeft: `2px solid ${isActive ? 'var(--fk-orange)' : 'var(--fk-border)'}`,
                        fontSize: 13,
                        lineHeight: 1.4,
                        textDecoration: 'none',
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? 'var(--fk-orange)' : 'var(--fk-text-2)',
                        transition: 'color var(--fk-dur-fast) var(--fk-ease), border-color var(--fk-dur-fast) var(--fk-ease)',
                      }}
                    >
                      {item.text}
                    </a>
                  );
                })}
              </div>
            </nav>
          )}

          {/* Актуальность (прототип FISHKA HUB): статус срока, владелец,
              периодичность, дата следующей проверки и просмотры. */}
          {data.status === 'PUBLISHED' && <FreshnessCard a={data} />}

          {data.changelog && data.changelog.length > 0 && (
            <div className="fk-kb-panel" style={{ padding: 18 }}>
              <div className="fk-kb-cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><HistoryOutlined /> История изменений</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.changelog.slice(0, 5).map((c) => (
                  <div key={c.version} style={{ fontSize: 12.5 }}>
                    <div style={{ color: 'var(--fk-text-muted)', fontSize: 11.5 }}>v{c.version}.0 · {fmtDate(c.at)}{c.authorName ? ` · ${c.authorName}` : ''}</div>
                    <div>{c.summary}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="fk-kb-panel" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(data.downloadable ?? data.attachments.length > 0) && data.attachments[0] && (
              <DsButton
                variant="ghost"
                size="sm"
                block
                icon={<DownloadOutlined />}
                href={`/api/wiki/attachments/${encodeURIComponent(data.attachments[0].id)}/download`}
              >
                Скачать вложение
              </DsButton>
            )}
            <DsButton variant="soft" size="sm" block icon={<ThunderboltOutlined />} onClick={() => navigate(`/ai?q=${encodeURIComponent(data.title)}`)}>
              Задать вопрос AI
            </DsButton>
            {canEdit && (
              <DsButton variant="ghost" size="sm" block icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
                История версий
              </DsButton>
            )}
            {canAdmin && data.type === 'decision_tree' && (
              <DsButton variant="ghost" size="sm" block icon={<EditOutlined />} to={`/admin/decision-tree/${encodeURIComponent(data.slug)}`}>
                Редактор сценария
              </DsButton>
            )}
          </div>

          {/* Метаданные статьи (передаём карту код→имя раздела для русских названий) */}
          <Sidebar article={data} blockNames={blockNames} />

          {/* «Есть курс по теме» — курсы, где статья стоит уроком/модулем. */}
          {((data as KbArticleDetail).relatedCourses ?? []).map((c) => (
            <Link key={c.id} to={`/course/${c.id}`} className="fk-kb-course-card">
              <ReadOutlined style={{ fontSize: 20 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>Есть курс по теме</div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
              </div>
            </Link>
          ))}

          {/* Страницы раздела (дерево) — раньше левая колонка, теперь внизу рейла. */}
          {showTree && firstBlock && (
            <nav ref={treeNavRef} className="fk-kb-panel article-tree" style={{ padding: '12px 4px 8px', maxHeight: 420, overflowY: 'auto' }} aria-label="Страницы раздела">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 12px 10px', borderBottom: '1px solid var(--fk-border)', marginBottom: 4 }}>
                <span style={{ fontSize: 16, color: 'var(--fk-orange)', display: 'flex' }}>{renderSectionIcon(sectionBlock?.icon)}</span>
                <span className="fk-kb-cap" style={{ margin: 0 }}>Страницы раздела</span>
              </div>
              <PageTree blockCode={firstBlock} />
            </nav>
          )}
        </aside>
        )}
      </div>

      {/* HUB 2.0: «Сделать курсом» и «Проверить на противоречия» */}
      {outlineOpen && (
        <CourseOutlineModal articleSlug={data.slug} articleId={data.id} canCreateCourse={canAdmin} onClose={() => setOutlineOpen(false)} />
      )}
      {conflictsOpen && <ConflictsModal slug={data.slug} onClose={() => setConflictsOpen(false)} />}

      {/* Модалки (логика сохранена) */}
      <Modal
        title="Отправить на проверку"
        open={reviewModalOpen}
        onCancel={() => setReviewModalOpen(false)}
        onOk={() => requestReviewMutation.mutate()}
        okButtonProps={{ disabled: !reviewerId }}
        confirmLoading={requestReviewMutation.isPending}
        okText="Отправить"
        cancelText="Отмена"
        destroyOnHidden
      >
        <p style={{ color: 'var(--fk-text-muted)', fontSize: 13 }}>Рецензент получит уведомление, сможет одобрить (опубликовать) или вернуть на доработку.</p>
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Начните вводить фамилию"
          filterOption={false}
          onSearch={setReviewerSearch}
          loading={reviewerOptionsQuery.isFetching}
          value={reviewerId ?? undefined}
          onChange={(v) => setReviewerId(v as string)}
          options={(reviewerOptionsQuery.data ?? []).map((u) => ({ value: u.id, label: u.fullName }))}
        />
      </Modal>
      <Modal
        title="Вернуть на доработку"
        open={returnNote != null}
        onCancel={() => setReturnNote(null)}
        onOk={() => returnMutation.mutate()}
        confirmLoading={returnMutation.isPending}
        okText="Вернуть"
        cancelText="Отмена"
        destroyOnHidden
      >
        <textarea value={returnNote ?? ''} onChange={(e) => setReturnNote(e.target.value)} rows={3} maxLength={1000}
          placeholder="Что поправить (автор получит уведомление)" className="fk-meta-input" style={{ width: '100%', resize: 'vertical' }} />
      </Modal>
      {canBroadcast && broadcastOpen && (
        <BroadcastModal open={broadcastOpen} onClose={() => setBroadcastOpen(false)} articleId={data.id} slug={data.slug} />
      )}
      {isCrm && suggestOpen && (
        <SuggestEditModal slug={data.slug} open={suggestOpen} onClose={() => setSuggestOpen(false)} />
      )}
      {canEdit && historyOpen && (
        <VersionHistory
          articleId={data.id}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onRolledBack={() => qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] })}
        />
      )}

      <style>{`
        @media (max-width: 1180px) {
          .article-layout:not(.article-layout--edit) { grid-template-columns: minmax(0, 1fr) 280px !important; gap: 20px !important; }
        }
        /* Узко — одна колонка, правый рейл уходит вниз */
        @media (max-width: 1024px) {
          .article-layout, .article-layout:not(.article-layout--edit) { grid-template-columns: minmax(0, 1fr) !important; }
          .article-rail { order: 2; position: static !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Правый рейл: метаданные статьи (реальные поля DTO) ──────────────
function Sidebar({ article, blockNames }: { article: WikiArticleDetailDto; blockNames: Map<string, string> }): JSX.Element {
  // Скрываем технические теги миграции (teamlyimport и прочие *import*) —
  // они служебные, в рейле статьи им не место.
  const visibleTags = article.tags.filter((t) => !/import/i.test(t.code));
  return (
    <div className="fk-kb-panel" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Блоки — показываем русское название раздела (фолбэк на код) */}
      {article.blocks.length > 0 && (
        <SidebarSection title="Блоки">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {article.blocks.map((b) => (
              <Link key={b} to={`/block/${b}`} style={{ textDecoration: 'none' }}>
                <Pill bg="var(--fk-orange-soft)" fg="var(--fk-orange)">{blockNames.get(b) ?? b}</Pill>
              </Link>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Категория */}
      {article.categoryName && (
        <SidebarSection title="Категория">
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--fk-text-2)', fontSize: 13 }}>
            <FolderOutlined style={{ color: 'var(--fk-orange)' }} /> {article.categoryName}
          </div>
        </SidebarSection>
      )}

      {/* Теги — человекочитаемое имя; технические *import*-теги скрыты */}
      {visibleTags.length > 0 && (
        <SidebarSection title="Теги">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {visibleTags.map((t) => (
              <Link key={t.code} to={`/search?q=${encodeURIComponent(t.name || t.code)}`} style={{ fontSize: 12, background: 'var(--fk-bg-subtle)', borderRadius: 'var(--fk-radius-sm)', padding: '3px 8px', color: 'var(--fk-text-2)', textDecoration: 'none' }}>
                #{t.name || t.code}
              </Link>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Backlinks */}
      {article.backlinks.length > 0 && (
        <SidebarSection title="Ссылаются на эту статью">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {article.backlinks.map((b) => (
              <Link key={b.id} to={`/article/${b.slug}`} style={{ fontSize: 13, color: 'var(--fk-orange)', textDecoration: 'none', display: 'block' }}>
                ↳ {b.title}
              </Link>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Похожие статьи */}
      {(article.related?.length ?? 0) > 0 && (
        <SidebarSection title="Похожие статьи">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {article.related!.map((r) => (
              <Link key={r.slug} to={`/article/${r.slug}`} style={{ fontSize: 13, fontWeight: 600, color: 'var(--fk-text)', textDecoration: 'none', lineHeight: 1.3 }}>
                {r.title}
              </Link>
            ))}
          </div>
        </SidebarSection>
      )}
    </div>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="fk-kb-cap">{title}</div>
      {children}
    </div>
  );
}

// ─── Бейдж/пилюля (как Badge в ките) ─────────────────────────────────
function Pill({ children, bg, fg, dot }: { children: React.ReactNode; bg: string; fg: string; dot?: boolean }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color: fg, padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.5 }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 999, background: fg }} />}
      {children}
    </span>
  );
}

// Аватар-инициалы автора в мета-строке (как в ките — кружок с буквами).
// Инициалы: первые буквы первых двух слов имени, в верхнем регистре.
function AuthorAvatar({ name }: { name: string }): JSX.Element {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 999,
        background: 'var(--fk-orange-soft)',
        color: 'var(--fk-orange)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10.5,
        fontWeight: 700,
        flexShrink: 0,
        lineHeight: 1,
      }}
    >
      {initials || '•'}
    </span>
  );
}

function ArticleTabs({ items }: { items: Array<{ key: string; label: string; html: string }> }): JSX.Element {
  return (
    <Tabs
      items={items.map((it) => ({
        key: it.key,
        label: it.label,
        children: <div className="wiki-content" dangerouslySetInnerHTML={{ __html: it.html }} />,
      }))}
    />
  );
}

/** Сетка эмодзи для выбора иконки статьи — тот же пикер, что в тулбаре редактора. */
function EmojiGrid({ onPick, onClear }: { onPick: (e: string) => void; onClear: () => void }): JSX.Element {
  return (
    <EmojiPicker
      width={330}
      onPick={onPick}
      header={
        <button type="button" className="fk-meta-chip" style={{ marginBottom: 8 }} onClick={onClear}>
          Без иконки
        </button>
      }
    />
  );
}

/** ISO → значение для <input type="datetime-local"> в локальном времени. */
function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const MATERIAL_LABELS: Record<string, string> = {
  article: 'Статья',
  instruction: 'Инструкция',
  checklist: 'Чек-лист',
  regulation: 'Регламент',
  faq: 'FAQ',
  standard: 'Стандарт',
  publication: 'Публикация',
};

// Иконка бейджа по типу материала — чтобы регламент/инструкция/FAQ визуально
// не сливались (раньше всё рисовалось одной иконкой).

// Статус-плашка: приоритет — подтверждённое ознакомление, иначе статус публикации.
function StatusPill({ status, acked }: { status: WikiArticleDetailDto['status']; acked: boolean }): JSX.Element {
  if (acked) return <DsTag color="green" icon={<CheckOutlined />}>Ознакомлен</DsTag>;
  const map: Record<WikiArticleDetailDto['status'], { color: 'green' | 'gold' | 'default'; label: string }> = {
    PUBLISHED: { color: 'green', label: 'Опубликовано' },
    REVIEW: { color: 'gold', label: 'На согласовании' },
    DRAFT: { color: 'default', label: 'Черновик' },
    ARCHIVED: { color: 'default', label: 'Архив' },
  };
  const st = map[status] ?? map.DRAFT;
  return <DsTag color={st.color}>{st.label}</DsTag>;
}

/**
 * HUB 2.0: карточка стандарта в рейле. УК (kb:admin) — «Подтвердили по сети
 * N %» (аудитория — пользователи CRM ролей стандарта) и «Напомнить
 * неподтвердившим» (раз в сутки). Остальные — подтверждение ознакомления.
 */
function StandardCard({
  a,
  canAdmin,
  isCrm,
  acking,
  onAck,
  reminding,
  onRemind,
}: {
  a: KbArticleDetail;
  canAdmin: boolean;
  isCrm: boolean;
  acking: boolean;
  onAck: () => void;
  reminding: boolean;
  onRemind: () => void;
}): JSX.Element {
  const net = a.networkAck ?? null;
  const pct = net?.pct ?? null;
  const ackText = a.isAcked
    ? 'Вы подтвердили ознакомление с текущей редакцией.'
    : a.needsReAck
      ? 'Стандарт обновлён — подтвердите ознакомление ещё раз.'
      : 'Прочитайте и подтвердите ознакомление — это обязательный стандарт сети.';
  return (
    <section className={`fk-kb-panel fk-kb-std-card${a.isAcked ? '' : ' is-pending'}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
        <SafetyCertificateOutlined style={{ color: 'var(--fk-orange)' }} />
        Стандарт{a.version ? ` · v${a.version}.0` : ''}
      </div>
      <div style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>{ackText}</div>
      {canAdmin && net && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: 'var(--fk-text-muted)' }}>Подтвердили по сети</span>
            <b>{pct == null ? '—' : `${pct}%`}</b>
          </div>
          <div className="fk-kb-bar">
            <i style={{ width: `${pct ?? 0}%`, background: kbAckColor(pct) }} />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', margin: '4px 0 10px' }}>
            {net.audience ? `${net.acked} из ${net.audience} пользователей CRM` : 'Аудитория стандарта пуста'}
          </div>
          {a.status === 'PUBLISHED' && net.audience > net.acked && (
            <DsButton variant="ghost" size="sm" block icon={<NotificationOutlined />} loading={reminding} onClick={onRemind}>
              Напомнить неподтвердившим
            </DsButton>
          )}
        </div>
      )}
      {!canAdmin && (
        <DsButton
          variant={a.isAcked ? 'soft' : 'primary'}
          block
          icon={<CheckOutlined />}
          disabled={a.isAcked || !isCrm}
          loading={acking}
          onClick={onAck}
        >
          {a.isAcked ? 'Ознакомление подтверждено' : 'Подтвердить ознакомление'}
        </DsButton>
      )}
      {!canAdmin && !isCrm && !a.isAcked && (
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>Подтверждение пока доступно только пользователям CRM.</div>
      )}
      {canAdmin && !a.isAcked && isCrm && (
        <button type="button" onClick={onAck} disabled={acking} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--fk-orange)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
          Подтвердить ознакомление самому
        </button>
      )}
    </section>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Подпись поля в панели метаданных инлайн-редактора. */

/**
 * Карточка «Актуальность» в правом рейле читателя (прототип FISHKA HUB).
 * Срок считается так же, как на бэкенде (wiki-verify.service): база —
 * дата последней проверки, если её не было — дата публикации.
 */
function FreshnessCard({ a }: { a: WikiArticleDetailDto }): JSX.Element | null {
  const days = a.verifyEveryDays ?? null;
  const base = a.verifiedAt ?? a.publishedAt ?? null;
  const due = days != null && base ? new Date(new Date(base).getTime() + days * 86_400_000) : null;
  const daysLeft = due ? Math.floor((due.getTime() - Date.now()) / 86_400_000) : null;
  const tone =
    daysLeft == null ? { bg: 'var(--fk-bg-subtle)', fg: 'var(--fk-text-muted)', text: 'Срок проверки не задан' }
      : daysLeft < 0 ? { bg: 'rgba(229,57,53,.10)', fg: 'var(--fk-error)', text: `Просрочено ${-daysLeft} дн.` }
        : daysLeft <= 14 ? { bg: 'var(--fk-orange-soft)', fg: 'var(--fk-orange)', text: `Проверить через ${daysLeft} дн.` }
          : { bg: 'rgba(124,179,66,.12)', fg: 'var(--fk-success)', text: 'Актуальна' };

  return (
    <div className="fk-kb-panel" style={{ padding: 18 }}>
      <div className="fk-kb-cap">Актуальность</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 'var(--fk-radius-md)', background: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
        {tone.text}
      </div>
      <div className="fk-fresh-rows">
        <div><span>Владелец</span><b>{a.ownerName || 'не назначен'}</b></div>
        {days != null && <div><span>Проверка</span><b>{verifyPeriodLabel(days)}</b></div>}
        {due && <div><span>Следующая</span><b>{fmtDate(due.toISOString())}</b></div>}
        {a.verifiedAt && <div><span>Проверена</span><b>{fmtDate(a.verifiedAt)}</b></div>}
        <div><span>Просмотры</span><b>{a.viewCount ?? 0}</b></div>
      </div>
      <style>{`
        .fk-fresh-rows{display:flex;flex-direction:column}
        .fk-fresh-rows>div{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:6px 0;font-size:12.5px;color:var(--fk-text-2);border-bottom:1px dashed var(--fk-border)}
        .fk-fresh-rows>div:last-child{border-bottom:none}
        .fk-fresh-rows b{color:var(--fk-text);text-align:right}
      `}</style>
    </div>
  );
}

/**
 * Плашка совместного редактирования (фаза 4.7).
 *
 * Показываем только когда есть что сказать: кто ещё правит статью прямо
 * сейчас. Пустая комната в realtime — молчим (лишний шум в интерфейсе), но
 * если WS не поднялся, честно предупреждаем: правки увидят не сразу, а
 * после сохранения, и одновременная правка вторым человеком затрёт текст.
 */
function CollabBar({ session }: { session: CollabSession }): JSX.Element | null {
  const { message } = App.useApp();
  if (session.state === 'offline') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, padding: '9px 13px', borderRadius: 'var(--fk-radius-md)', background: 'var(--fk-bg-subtle)', color: 'var(--fk-text-2)', fontSize: 12.5, lineHeight: 1.5 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--fk-text-muted)', flexShrink: 0 }} />
        Совместное редактирование недоступно. Избегайте одновременной правки этой статьи.
      </div>
    );
  }
  if (session.peers.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '9px 13px', borderRadius: 'var(--fk-radius-md)', background: 'rgba(124,179,66,.12)', color: 'var(--fk-text)', fontSize: 12.5, flexWrap: 'wrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--fk-success)', flexShrink: 0 }} />
      <span style={{ fontWeight: 700 }}>Сейчас правят вместе с вами:</span>
      {/* Клик по имени — прокрутить к месту, где человек сейчас правит или
          держит мышь (длинная статья: сосед может быть далеко внизу). */}
      {session.peers.map((p) => (
        <button
          key={p.clientId}
          type="button"
          title={`Показать, где сейчас ${p.name}`}
          onClick={() => {
            if (!scrollToCollabPeer(p.clientId)) message.info(`${p.name} сейчас не в тексте статьи`);
          }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 9px 2px 3px', borderRadius: 999, background: 'var(--fk-surface)', border: '1px solid var(--fk-border)', fontWeight: 600, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
        >
          <span aria-hidden style={{ width: 18, height: 18, borderRadius: '50%', background: p.color, color: '#fff', fontSize: 9, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {p.name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')}
          </span>
          {p.name}
        </button>
      ))}
      <span style={{ color: 'var(--fk-text-muted)' }}>правки видны сразу, история — общая; нажмите на имя — покажем, где человек</span>
    </div>
  );
}

function MetaLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>
      {children}
    </div>
  );
}


// ─── Навигация по соседним статьям раздела (← Предыдущая / Следующая →) ──
/**
 * Карточки перехода к предыдущей/следующей статье блока. Порядок — как в
 * дереве «Страницы» (PageTree): статьи группируются по категории (ROOT
 * первой, далее по compareSectionNames), внутри группы — по sortOrder; всё
 * флатится в линейную последовательность. Переиспользует тот же queryKey,
 * что и PageTree, → общий кеш, без лишнего запроса.
 */
function PrevNextNav({ blockCode, currentSlug }: { blockCode: string; currentSlug: string }): JSX.Element | null {
  const q = useQuery({
    queryKey: ['wiki', 'articles', { blockCode, sort: 'order' }],
    queryFn: () => wikiApi.listArticles({ blockCode, limit: 200, sort: 'order' }),
    enabled: !!blockCode,
    staleTime: 30_000,
  });
  // Категории с sortOrder — для порядка групп по ручному (drag) порядку.
  const catsQ = useQuery({
    queryKey: ['wiki', 'categories', blockCode],
    queryFn: () => wikiApi.listCategories(blockCode),
    enabled: !!blockCode,
    staleTime: 30_000,
  });

  const ordered = useMemo<WikiArticleListItem[]>(() => {
    const items = q.data?.items ?? [];
    const ROOT = '__root__';
    const order = new Map<string, number>();
    for (const c of catsQ.data ?? []) {
      const prev = order.get(c.name);
      if (prev == null || c.sortOrder < prev) order.set(c.name, c.sortOrder);
    }
    const cmp = makeSectionComparator(order);
    const map = new Map<string, WikiArticleListItem[]>();
    for (const a of items) {
      const key = a.categoryName ?? ROOT;
      const arr = map.get(key) ?? [];
      arr.push(a);
      map.set(key, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);
    const groups = [...map.entries()].sort((x, y) => {
      if (x[0] === ROOT) return -1;
      if (y[0] === ROOT) return 1;
      return cmp(x[0], y[0]);
    });
    return groups.flatMap(([, arr]) => arr);
  }, [q.data, catsQ.data]);

  const idx = ordered.findIndex((a) => a.slug === currentSlug);
  if (idx < 0) return null;
  const prev = idx > 0 ? ordered[idx - 1] : null;
  const next = idx < ordered.length - 1 ? ordered[idx + 1] : null;
  if (!prev && !next) return null;

  return (
    <nav
      aria-label="Соседние статьи раздела"
      style={{
        marginTop: 36,
        paddingTop: 20,
        borderTop: '1px solid var(--fk-border)',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 14,
      }}
    >
      {prev ? (
        <Link to={`/article/${prev.slug}`} className="fk-prevnext" style={{ textAlign: 'left' }}>
          <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', fontWeight: 600 }}>‹ Предыдущая</span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--fk-text)' }}>{prev.title}</span>
        </Link>
      ) : (
        <span />
      )}
      {next ? (
        <Link to={`/article/${next.slug}`} className="fk-prevnext" style={{ textAlign: 'right', alignItems: 'flex-end' }}>
          <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', fontWeight: 600 }}>Следующая ›</span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--fk-text)' }}>{next.title}</span>
        </Link>
      ) : (
        <span />
      )}
      <style>{`
        .fk-prevnext{display:flex;flex-direction:column;gap:6px;padding:14px 18px;border:1px solid var(--fk-border);border-radius:var(--fk-radius-md);background:var(--fk-surface);text-decoration:none;transition:border-color .12s var(--fk-ease),box-shadow .12s var(--fk-ease)}
        .fk-prevnext:hover{border-color:var(--fk-orange);box-shadow:var(--fk-shadow-card)}
        /* Только контур на ховере — без подчёркивания/смены цвета текста
           (перебиваем глобальный a:hover). */
        .fk-prevnext:hover,.fk-prevnext:hover *{text-decoration:none !important}
        @media (max-width:640px){ nav[aria-label="Соседние статьи раздела"]{grid-template-columns:1fr !important} }
      `}</style>
    </nav>
  );
}
